import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PromoCodeInput from '../components/PromoCodeInput';
import PayPalCheckout from '../components/PayPalCheckout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const XTREAM_APP_NAME = 'Fluxivo';
const XTREAM_PUBLIC_URL = 'https://fluxivo.de';

const DEFAULT_RESELLER_PLANS = {
  premium:     { price: 2000,  duration: 30,  name: 'Premium - 1 Mois',     connections: 1 },
  pro:         { price: 5000,  duration: 90,  name: 'Pro - 3 Mois',         connections: 1 },
  semi_annual: { price: 7500,  duration: 180, name: 'Semi-Annuel - 6 Mois', connections: 2 },
  ultimate:    { price: 10500, duration: 365, name: 'Ultimate - 1 An',      connections: 2 }
};

const formatXtreamCredentials = ({ username, password, url = XTREAM_PUBLIC_URL }) => (
  `Name: ${XTREAM_APP_NAME}\nUtilisateur: ${username || ''}\nMot de passe: ${password || ''}\nURL Xtream: ${url || XTREAM_PUBLIC_URL}`
);

const getM3uUrl = ({ username, password, m3u_short, m3uUrl, short_token } = {}) => {
  if (m3u_short) return m3u_short;
  if (m3uUrl) return m3uUrl;
  if (short_token) return `${XTREAM_PUBLIC_URL}/m/${short_token}`;
  if (username && password) {
    return `${XTREAM_PUBLIC_URL}/get.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&type=m3u_plus&output=m3u8`;
  }
  return '';
};

const formatXtreamAndM3u = ({ username, password, m3u_short, m3uUrl, short_token } = {}) => {
  const m3u = getM3uUrl({ username, password, m3u_short, m3uUrl, short_token });
  return `${formatXtreamCredentials({ username, password })}${m3u ? `\nLien M3U: ${m3u}` : ''}`;
};

const formatDeviceLimit = (value) => {
  const count = Math.max(1, parseInt(value, 10) || 1);
  return `${count} appareil${count > 1 ? 's' : ''}`;
};

const formatCategoryKey = (cat) => `${cat.category_type || 'live'}:${cat.category_id}`;

const getCategoryLabel = (cat) => {
  if (cat.category_type === 'vod') return `${cat.category_name} (Films)`;
  if (cat.category_type === 'series') return `${cat.category_name} (Séries)`;
  return cat.category_name;
};

const getContentTypeLabel = (type) => {
  if (type === 'vod') return 'Film';
  if (type === 'series') return 'Série';
  return 'Live';
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ResellerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [error, setError] = useState('');
  const [reseller, setReseller] = useState(null);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [resellerPlans, setResellerPlans] = useState(DEFAULT_RESELLER_PLANS);

  // ===== Système de notifications/modals personnalisé =====
  const [notif, setNotif] = useState(null); // { type: 'info'|'success'|'error'|'warning'|'confirm', title, message, onConfirm }
  const showNotif = (type, title, message) => setNotif({ type, title, message });
  const showConfirm = (title, message, onConfirm) => setNotif({ type: 'confirm', title, message, onConfirm });
  const closeNotif = () => setNotif(null);
  // Remplacements de alert() et confirm()
  const rdAlert = (message, type = 'info') => {
    const isSuccess = message.startsWith('✅');
    const isError = message.startsWith('❌') || message.toLowerCase().includes('erreur');
    const isWarning = message.startsWith('⚠️') || message.startsWith('🔒');
    const t = type !== 'info' ? type : isSuccess ? 'success' : isError ? 'error' : isWarning ? 'warning' : 'info';
    const lines = message.split('\n').filter(l => l.trim());
    const title = lines[0];
    const body = lines.slice(1).join('\n');
    showNotif(t, title, body);
  };
  const rdConfirm = (message, onConfirm) => {
    const lines = message.split('\n').filter(l => l.trim());
    showConfirm(lines[0], lines.slice(1).join('\n'), onConfirm);
  };
  // ===================================================
  const [clients, setClients] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    payment_method: 'momo',
    payment_details: '',
    withdraw_mode: 'mtn',
    country_code: 'CM'
  });
  const [deletingClientId, setDeletingClientId] = useState(null);
  const [generate24hForm, setGenerate24hForm] = useState({
    clientName: ''
  });
  const [generating24h, setGenerating24h] = useState(false);
  const [generated24hCode, setGenerated24hCode] = useState(null);
  const [clientActionLoading, setClientActionLoading] = useState({});
  const [blockCatModal, setBlockCatModal] = useState(null);
  const [blockedCats, setBlockedCats] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [catSearch, setCatSearch] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);
  const [connectionModal, setConnectionModal] = useState(null);
  const [connectionData, setConnectionData] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(false);
  
  // États pour le système de crédit
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditEnabled, setCreditEnabled] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [depositForm, setDepositForm] = useState({
    amount: '',
    whatsappNumber: ''
  });
  const [creditSubForm, setCreditSubForm] = useState({
    clientName: '',
    whatsappNumber: '',
    plan: '1month'
  });
  const [submittingDeposit, setSubmittingDeposit] = useState(false);
  const [creatingCreditSub, setCreatingCreditSub] = useState(false);
  const [generatedAccount, setGeneratedAccount] = useState(null);
  const [aggregatorsConfig, setAggregatorsConfig] = useState(null);
  const [countriesCatalog, setCountriesCatalog] = useState([]);
  const [selectedAggregator, setSelectedAggregator] = useState(null);

  // États pour le prolongement
  const [renewModal, setRenewModal] = useState(null); // client object or null
  const [renewPlan, setRenewPlan] = useState('premium');
  const [renewPromoData, setRenewPromoData] = useState(null);
  const [renewLoading, setRenewLoading] = useState(false);

  // États méthodes de paiement
  const [membershipPayMethod, setMembershipPayMethod] = useState('momo');
  const [renewPayMethod, setRenewPayMethod] = useState('momo');
  const [depositPayMethod, setDepositPayMethod] = useState('momo');
  const [membershipCardDone, setMembershipCardDone] = useState(false);
  const [renewCardDone, setRenewCardDone] = useState(false);
  const [depositCardDone, setDepositCardDone] = useState(false);
  const [ppError, setPpError] = useState('');
  const [renewalBanner, setRenewalBanner] = useState(null); // { clientName, expiryDate } or null

  const PAYOUT_COUNTRIES = [
    { code: 'CM', flag: '🇨🇲', name: 'Cameroun',          defaultMode: 'mtn' },
    { code: 'SN', flag: '🇸🇳', name: 'Sénégal',           defaultMode: 'wave' },
    { code: 'CI', flag: '🇨🇮', name: "Côte d'Ivoire",     defaultMode: 'orange-money' },
    { code: 'BJ', flag: '🇧🇯', name: 'Bénin',             defaultMode: 'mtn' },
    { code: 'BF', flag: '🇧🇫', name: 'Burkina Faso',      defaultMode: 'orange-money' },
    { code: 'ML', flag: '🇲🇱', name: 'Mali',              defaultMode: 'orange-money' },
    { code: 'TG', flag: '🇹🇬', name: 'Togo',              defaultMode: 't-money' },
    { code: 'GN', flag: '🇬🇳', name: 'Guinée Conakry',    defaultMode: 'orange-money' },
    { code: 'GW', flag: '🇬🇼', name: 'Guinée-Bissau',     defaultMode: 'mtn' },
    { code: 'NE', flag: '🇳🇪', name: 'Niger',             defaultMode: 'airtel' },
    { code: 'GA', flag: '🇬🇦', name: 'Gabon',             defaultMode: 'airtel' },
    { code: 'CG', flag: '🇨🇬', name: 'Congo Brazzaville', defaultMode: 'mtn' },
    { code: 'CD', flag: '🇨🇩', name: 'Congo RDC',         defaultMode: 'airtel' },
    { code: 'TD', flag: '🇹🇩', name: 'Tchad',             defaultMode: 'airtel' },
    { code: 'CF', flag: '🇨🇫', name: 'Centrafrique',      defaultMode: 'orange-money' },
    { code: 'MR', flag: '🇲🇷', name: 'Mauritanie',        defaultMode: 'bankily' },
    { code: 'GH', flag: '🇬🇭', name: 'Ghana',             defaultMode: 'mtn' },
    { code: 'KE', flag: '🇰🇪', name: 'Kenya',             defaultMode: 'm-pesa' },
    { code: 'UG', flag: '🇺🇬', name: 'Ouganda',           defaultMode: 'mtn' },
    { code: 'RW', flag: '🇷🇼', name: 'Rwanda',            defaultMode: 'mtn' },
    { code: 'TZ', flag: '🇹🇿', name: 'Tanzanie',          defaultMode: 'airtel' },
    { code: 'SL', flag: '🇸🇱', name: 'Sierra Leone',      defaultMode: 'africell' },
    { code: 'GM', flag: '🇬🇲', name: 'Gambie',            defaultMode: 'orange-money' },
    { code: 'ET', flag: '🇪🇹', name: 'Éthiopie',          defaultMode: 'safaricom' },
  ];

  const COUNTRY_OPERATORS = {
    'CM': [
      { value: 'mtn',          label: 'MTN Mobile Money' },
      { value: 'orange-money', label: 'Orange Money' },
    ],
    'SN': [
      { value: 'wave',         label: 'Wave' },
      { value: 'orange-money', label: 'Orange Money' },
      { value: 'free-money',   label: 'Free Money' },
      { value: 'expresso',     label: 'Expresso Money' },
    ],
    'CI': [
      { value: 'orange-money', label: 'Orange Money' },
      { value: 'mtn',          label: 'MTN MoMo' },
      { value: 'moov',         label: 'Moov Money' },
      { value: 'wave',         label: 'Wave' },
    ],
    'BJ': [
      { value: 'mtn',  label: 'MTN Mobile Money' },
      { value: 'moov', label: 'Moov Money (Flooz)' },
    ],
    'BF': [
      { value: 'orange-money', label: 'Orange Money' },
      { value: 'moov',         label: 'Moov Money' },
    ],
    'ML': [
      { value: 'orange-money', label: 'Orange Money' },
    ],
    'TG': [
      { value: 't-money', label: 'T-Money (Togocel)' },
      { value: 'moov',    label: 'Flooz (Moov)' },
    ],
    'GN': [
      { value: 'orange-money', label: 'Orange Money' },
      { value: 'mtn',          label: 'MTN MoMo' },
    ],
    'GW': [
      { value: 'mtn', label: 'MTN Mobile Money' },
    ],
    'NE': [
      { value: 'airtel',   label: 'Airtel Money' },
      { value: 'mtn',      label: 'MTN Money' },
      { value: 'mauritel', label: 'Mauritel' },
    ],
    'GA': [
      { value: 'airtel', label: 'Airtel Money' },
      { value: 'moov',   label: 'Moov Money (Libertis)' },
    ],
    'CG': [
      { value: 'mtn',          label: 'MTN MoMo' },
      { value: 'orange-money', label: 'Orange Money' },
    ],
    'CD': [
      { value: 'airtel', label: 'Airtel Money' },
      { value: 'm-pesa', label: 'M-Pesa (Vodacom)' },
    ],
    'TD': [
      { value: 'airtel', label: 'Airtel Money' },
      { value: 'moov',   label: 'Moov Money' },
    ],
    'CF': [
      { value: 'orange-money', label: 'Orange Money' },
    ],
    'MR': [
      { value: 'bankily', label: 'Bankily' },
    ],
    'GH': [
      { value: 'mtn',       label: 'MTN MoMo' },
      { value: 'airtel',    label: 'AirtelTigo Money' },
      { value: 'vodafone',  label: 'Vodafone Cash' },
    ],
    'KE': [
      { value: 'm-pesa', label: 'M-Pesa (Safaricom)' },
    ],
    'UG': [
      { value: 'mtn', label: 'MTN Mobile Money' },
    ],
    'RW': [
      { value: 'mtn', label: 'MTN Mobile Money' },
    ],
    'TZ': [
      { value: 'airtel',  label: 'Airtel Money' },
      { value: 'm-pesa',  label: 'M-Pesa (Vodacom)' },
      { value: 'tigo',    label: 'Tigo Pesa' },
    ],
    'SL': [
      { value: 'africell',     label: 'Africell Money' },
      { value: 'orange-money', label: 'Orange Money' },
    ],
    'GM': [
      { value: 'orange-money', label: 'Orange Money' },
    ],
    'ET': [
      { value: 'safaricom', label: 'Safaricom M-Pesa' },
    ],
  };

  const CREDIT_PLANS = {
    '1month':  { price: 2000,  duration: 30,  name: '1 Mois' },
    '3months': { price: 5000,  duration: 90,  name: '3 Mois' },
    '6months': { price: 7500,  duration: 180, name: '6 Mois' },
    '1year':   { price: 10500, duration: 365, name: '1 An' }
  };

  // Templates de messages WhatsApp
  const messageTemplates = {
    reminder: {
      name: '🔔 Rappel renouvellement',
      message: (client) => `Bonjour ${client.nom},\n\nVotre abonnement Fluxivo arrive à expiration ! 📅\n\nRenouvelez dès maintenant pour continuer à profiter de vos chaînes préférées.\n\n✨ Contactez-moi pour renouveler.\n\nMerci de votre confiance ! 🙏`
    },
    welcome: {
      name: '👋 Message de bienvenue',
      message: (client) => `Bonjour ${client.nom},\n\nBienvenue sur Fluxivo ! 🎉\n\nVotre abonnement est maintenant actif.\n\n📱 Vos identifiants ont été envoyés par email.\n\nBesoin d'aide ? Répondez à ce message.\n\nBon visionnage ! 📺`
    },
    support: {
      name: '💬 Message de support',
      message: (client) => `Bonjour ${client.nom},\n\nComment puis-je vous aider ? 😊\n\n💡 Je suis disponible pour toute question concernant votre abonnement Fluxivo.\n\nRépondez à ce message ! 📱`
    },
    credentials: {
      name: '🔑 Renvoyer identifiants',
      message: (client) => `Bonjour ${client.nom},\n\nVoici vos identifiants Fluxivo:\n\n${formatXtreamAndM3u({
        username: client.xtream_username || '[Voir email]',
        password: client.xtream_password || '[Voir email]',
        m3u_short: client.m3u_short,
        short_token: client.xtream_short_token
      })}\n\nBon visionnage ! 📺`
    },
    custom: {
      name: '✏️ Message personnalisé',
      message: (client) => `Bonjour ${client.nom},\n\n`
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('resellerToken');
    if (!token) {
      navigate('/reseller/login');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Agrégateurs Mobile Money proposables (config admin).
  useEffect(() => {
    axios.get(`${API_URL}/payments/aggregators`)
      .then(({ data }) => { setAggregatorsConfig(data.aggregators || null); setCountriesCatalog(data.countries || []); })
      .catch(() => { /* non bloquant */ });
  }, []);

  // Détecter le retour depuis un paiement de prolongement
  useEffect(() => {
    // MF redirige avec notre token interne dans l'URL, mais on a aussi sauvegardé le token MF
    const urlToken = searchParams.get('token') || searchParams.get('renewal_token');
    if (!urlToken) return;

    // Utiliser le token MF (sauvegardé avant redirect) en priorité, sinon celui de l'URL
    const mfToken = localStorage.getItem('renewal_mf_token');
    const renewalToken = mfToken || urlToken;
    localStorage.removeItem('renewal_mf_token');

    // Nettoyer l'URL
    setSearchParams({});

    const pollRenewal = async () => {
      try {
        const res = await axios.get(`${API_URL}/payments/status/${renewalToken}`);
        if (res.data.status === 'paid') {
          const clientName = localStorage.getItem('renewal_client_name') || 'Client';
          localStorage.removeItem('renewal_client_name');
          setRenewalBanner({
            clientName,
            expiryDate: res.data.expiry_date,
            xtreamUsername: res.data.xtream?.xtreamUsername,
            xtreamPassword: res.data.xtream?.xtreamPassword
          });
          setActiveTab('clients');
          loadData();
        } else if (res.data.status === 'pending') {
          setTimeout(pollRenewal, 4000);
        }
      } catch (e) {
        // Essai avec l'autre token si le premier échoue
        if (mfToken && mfToken !== urlToken) {
          try {
            const res2 = await axios.get(`${API_URL}/payments/status/${urlToken}`);
            if (res2.data.status === 'paid') {
              const clientName = localStorage.getItem('renewal_client_name') || 'Client';
              localStorage.removeItem('renewal_client_name');
              setRenewalBanner({ clientName, expiryDate: res2.data.expiry_date, xtreamUsername: res2.data.xtream?.xtreamUsername, xtreamPassword: res2.data.xtream?.xtreamPassword });
              setActiveTab('clients');
              loadData();
            } else if (res2.data.status === 'pending') {
              setTimeout(pollRenewal, 4000);
            }
          } catch { /* ignore */ }
        }
        console.error('Erreur vérification prolongement:', e);
      }
    };
    pollRenewal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Détecter le retour depuis XPAYE.
  useEffect(() => {
    const ppRef = searchParams.get('pp_ref');
    if (!ppRef) return;
    const ppType = searchParams.get('pp_type') || localStorage.getItem('pp_type') || 'generate';
    localStorage.removeItem('pp_ref');
    localStorage.removeItem('pp_type');
    setSearchParams({});

    let cancelled = false;
    let pollAttempts = 0;
    const maxPollAttempts = 100;
    const pollPp = async () => {
      pollAttempts += 1;
      try {
        const token = localStorage.getItem('resellerToken');
        if (!token) return;
        const res = await axios.get(`${API_URL}/resellers/xpaye/status/${ppRef}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        if (res.data.status === 'paid') {
          if (ppType === 'membership') {
            rdAlert('✅ Adhésion activée avec succès !', 'success');
            setActiveTab('overview');
          } else if (ppType === 'deposit') {
            rdAlert('✅ Crédit ajouté avec succès !', 'success');
            setActiveTab('credit');
          } else if (ppType === 'renewal') {
            setRenewalBanner({ clientName: res.data.clientName || 'Client', expiryDate: res.data.expiry_date, xtreamUsername: res.data.xtream?.xtreamUsername, xtreamPassword: res.data.xtream?.xtreamPassword });
            setActiveTab('clients');
          } else {
            rdAlert('✅ Compte généré et paiement confirmé !', 'success');
            setActiveTab('clients');
          }
          loadData();
        } else if (res.data.status === 'failed') {
          rdAlert('❌ Le paiement XPAYE a échoué ou a été annulé.', 'error');
        } else if (pollAttempts >= maxPollAttempts) {
          rdAlert('⚠️ Le paiement XPAYE est toujours en attente de confirmation. Il sera visible dès réception de la notification.', 'warning');
        } else {
          setTimeout(pollPp, 3000);
        }
      } catch (e) {
        if (!cancelled && pollAttempts < maxPollAttempts) {
          setTimeout(pollPp, 4000);
        } else if (!cancelled) {
          rdAlert('⚠️ Impossible de confirmer automatiquement le paiement XPAYE. Vérifiez son statut avant de recommencer.', 'warning');
        }
      }
    };
    pollPp();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderPaypalCheckout = (type, payload = {}) => {
    const paths = {
      membership: ['/resellers/membership/paypal-create-order', '/resellers/membership/paypal-capture'],
      renewal: ['/resellers/paypal/renewal-create-order', '/resellers/paypal/renewal-capture'],
      deposit: ['/resellers/credit/paypal-create-order', '/resellers/credit/paypal-capture']
    };
    const headers = { Authorization: `Bearer ${localStorage.getItem('resellerToken')}` };
    return <div style={{ width: 'min(100%, 340px)', minWidth: '200px' }}>
      <PayPalCheckout
        createOrder={async () => {
          const response = await axios.post(`${API_URL}${paths[type][0]}`, payload, { headers });
          return response.data.orderId;
        }}
        captureOrder={async orderId => {
          const response = await axios.post(`${API_URL}${paths[type][1]}`, { orderId }, { headers });
          return response.data;
        }}
        onSuccess={result => {
          if (type === 'membership') setMembershipCardDone(true);
          if (type === 'renewal') {
            setRenewCardDone(true);
            setRenewalBanner({ clientName: renewModal?.nom || 'Client', expiryDate: result.expiry_date });
          }
          if (type === 'deposit') setDepositCardDone(true);
          setPpError('');
          loadData();
        }}
        onError={err => setPpError(err.response?.data?.error || err.message || 'Paiement PayPal impossible')}
      />
    </div>;
  };

  const handleRenewal = async () => {
    if (!renewModal) return;
    setRenewLoading(true);
    try {
      const resellerToken = localStorage.getItem('resellerToken');
      const resellerId = reseller?.id;

      const response = await axios.post(`${API_URL}/payments/initiate`, {
        plan_type: renewPlan,
        promo_code: renewPromoData?.code || undefined,
        numeroSend: renewModal.telephone || '00000000',
        nomclient: renewModal.nom,
        email: renewModal.email,
        reseller_id: resellerId,
        return_path: '/reseller/dashboard',
        whatsappNumber: renewModal.telephone || ''
      }, {
        headers: { Authorization: `Bearer ${resellerToken}` }
      });

      if (response.data.success && response.data.paymentUrl) {
        localStorage.setItem('renewal_client_name', renewModal.nom);
        const paymentUrl = response.data.paymentUrl;
        localStorage.setItem('renewal_mf_token', response.data.token);
        setRenewModal(null);
        window.location.href = paymentUrl;
      } else {
        rdAlert('Erreur lors de l\'initiation du paiement: ' + (response.data.message || 'Inconnu'), 'error');
      }
    } catch (err) {
      console.error('Erreur prolongement:', err);
      rdAlert('Erreur: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setRenewLoading(false);
    }
  };

  const handleCreditRenewal = async () => {
    if (!renewModal) return;
    const selectedPlan = resellerPlans[renewPlan];

    if (!creditEnabled) {
      rdAlert('Le paiement par crédit n’est pas activé pour votre compte revendeur.', 'warning');
      return;
    }

    const requiredCredit = renewPromoData?.finalPrice ?? selectedPlan.price;
    if (requiredCredit > creditBalance) {
      rdAlert(`Crédit insuffisant. Plan: ${requiredCredit.toLocaleString()} FCFA - Disponible: ${creditBalance.toLocaleString()} FCFA`, 'error');
      return;
    }

    rdConfirm(`Prolonger ${renewModal.nom} avec votre crédit ?\n${selectedPlan.name} - ${selectedPlan.price.toLocaleString()} FCFA`, async () => {
      setRenewLoading(true);
      try {
        const token = localStorage.getItem('resellerToken');
        const response = await axios.post(`${API_URL}/resellers/credit/renew-subscription`, {
          client_id: renewModal.id,
          plan_type: renewPlan,
          promo_code: renewPromoData?.code || undefined
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setRenewModal(null);
        setRenewCardDone(false);
        setRenewalBanner({
          clientName: response.data.client_name || renewModal.nom,
          expiryDate: response.data.expires_at,
          xtreamUsername: response.data.xtream?.username,
          xtreamPassword: response.data.xtream?.password
        });
        setActiveTab('clients');
        const headers = { Authorization: `Bearer ${token}` };
        loadCreditData(headers);
        loadData();
      } catch (err) {
        rdAlert('Erreur: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        setRenewLoading(false);
      }
    });
  };

  const handleGenerate24hCode = async (e) => {
    e.preventDefault();
    
    if (!membershipStatus?.membership_paid) {
      rdAlert(`🔒 Adhésion requise\n\nActivez votre adhésion (${membershipStatus?.membership_total?.toLocaleString() || '...'} FCFA) pour utiliser cette fonctionnalité.\n\nCliquez sur "💳 Payer l'adhésion" dans le bandeau en haut de page.`, 'warning');
      return;
    }

    if (!generate24hForm.clientName) {
      rdAlert('Le nom du client est requis', 'warning');
      return;
    }

    const confirmMsg = `Générer un code DÉMO de 24h pour ${generate24hForm.clientName}?\n\nCe code est gratuit et expire après 24 heures.`;
    rdConfirm(confirmMsg, async () => {
      setGenerating24h(true);
      const token = localStorage.getItem('resellerToken');
      try {
        const response = await axios.post(
          `${API_URL}/resellers/generate-24h-code`,
          { clientName: generate24hForm.clientName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          setGenerated24hCode(response.data.account);
          rdAlert('✅ Code de 24h généré avec succès !', 'success');
          loadData();
        }
      } catch (err) {
        console.error('Erreur génération 24h:', err);
        rdAlert(err.response?.data?.error || 'Erreur lors de la génération du code 24h', 'error');
      } finally {
        setGenerating24h(false);
      }
    });
  };

  const closeGenerated24hModal = () => {
    setGenerated24hCode(null);
    setGenerate24hForm({
      clientName: ''
    });
  };

  // Fonction robuste pour copier dans le presse-papier
  const safeCopyToClipboard = async (text) => {
    try {
      // Méthode 1: Navigator Clipboard API (moderne)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      console.log('Clipboard API failed, using fallback');
    }
    
    // Méthode 2: Fallback avec textarea
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      return false;
    }
  };

  const copyToClipboard = async (text, label) => {
    const success = await safeCopyToClipboard(text);
    if (success) {
      rdAlert(`✅ ${label} copié !`, 'success');
    } else {
      rdAlert(`⚠️ Impossible de copier automatiquement. Veuillez copier manuellement :\n\n${text}`, 'warning');
    }
  };

  const loadMessages = async (headersArg = null) => {
    try {
      const token = localStorage.getItem('resellerToken');
      if (!token && !headersArg?.Authorization) return;

      const headers = headersArg || { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/resellers/messages`, { headers });

      setMessages(response.data.messages || []);
      setMessageUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    }
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('resellerToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [profileRes, statsRes, clientsRes, commissionsRes, withdrawalsRes, membershipRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/resellers/profile`, { headers }),
        axios.get(`${API_URL}/resellers/stats`, { headers }),
        axios.get(`${API_URL}/resellers/clients`, { headers }),
        axios.get(`${API_URL}/resellers/commissions?limit=20`, { headers }),
        axios.get(`${API_URL}/resellers/withdrawals`, { headers }),
        axios.get(`${API_URL}/resellers/membership/status`, { headers }),
        axios.get(`${API_URL}/resellers/plans`, { headers }).catch(error => {
          console.error('Erreur chargement tarifs revendeur:', error);
          return null;
        })
      ]);

      setReseller(profileRes.data.reseller);
      setStats(statsRes.data.stats);
      setClients(clientsRes.data.clients);
      setCommissions(commissionsRes.data.commissions);
      setWithdrawals(withdrawalsRes.data.withdrawals);
      setMembershipStatus(membershipRes.data);

      const configuredPlans = plansRes?.data?.plans;
      if (configuredPlans && Object.keys(configuredPlans).length > 0) {
        const firstPlanType = Object.keys(configuredPlans)[0];
        setResellerPlans(configuredPlans);
        setRenewPlan(previous => configuredPlans[previous] ? previous : firstPlanType);
      }
      
      // Charger les données de crédit si le système est activé
      if (profileRes.data.reseller.credit_system_enabled === 1) {
        loadCreditData(headers);
      }

      loadMessages(headers);
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setError(error.response?.data?.error || 'Erreur de chargement des données');
      setLoading(false);
      if (error.response?.status === 401) {
        localStorage.removeItem('resellerToken');
        navigate('/reseller/login');
      }
    }
  };

  const loadCreditData = async (headers) => {
    try {
      const [balanceRes, depositsRes, transactionsRes] = await Promise.all([
        axios.get(`${API_URL}/resellers/credit/balance`, { headers }),
        axios.get(`${API_URL}/resellers/credit/deposits`, { headers }),
        axios.get(`${API_URL}/resellers/credit/transactions`, { headers })
      ]);

      setCreditBalance(balanceRes.data.balance);
      setCreditEnabled(balanceRes.data.enabled);
      setDeposits(depositsRes.data);
      setCreditTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Erreur chargement données crédit:', error);
    }
  };

  const resellerAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('resellerToken')}`
  });

  const setClientBusy = (clientId, action, value) => {
    const key = `${clientId}:${action}`;
    setClientActionLoading(prev => {
      const next = { ...prev };
      if (value) next[key] = true;
      else delete next[key];
      return next;
    });
  };

  const isClientBusy = (clientId, action) => Boolean(clientActionLoading[`${clientId}:${action}`]);

  const updateClient = (clientId, updater) => {
    setClients(prev => prev.map(client => (
      client.id === clientId ? { ...client, ...updater(client) } : client
    )));
  };

  const updateClientBlockedCount = (clientId, count) => {
    updateClient(clientId, () => ({ xtream_blocked_categories_count: count }));
  };

  const handleToggleClientPause = (client) => {
    if (!client?.xtream_id) {
      rdAlert('Aucun compte Xtream trouvé pour ce client', 'warning');
      return;
    }

    const isPaused = client.xtream_is_enabled === 0;
    rdConfirm(
      `${isPaused ? 'Réactiver' : 'Mettre en pause'} ${client.nom || client.xtream_username} ?\n${isPaused ? 'Le client pourra à nouveau utiliser ses accès Xtream.' : 'Ses accès Xtream seront désactivés immédiatement.'}`,
      async () => {
        setClientBusy(client.id, 'pause', true);
        try {
          const response = await axios.put(
            `${API_URL}/resellers/clients/${client.id}/xtream/toggle`,
            {},
            { headers: resellerAuthHeaders() }
          );
          updateClient(client.id, () => ({ xtream_is_enabled: response.data.is_enabled }));
          rdAlert(`✅ ${response.data.message || 'Compte mis à jour'}`, 'success');
        } catch (error) {
          rdAlert(error.response?.data?.error || 'Erreur lors de la modification du compte', 'error');
        } finally {
          setClientBusy(client.id, 'pause', false);
        }
      }
    );
  };

  const handleResetClientPassword = (client) => {
    if (!client?.xtream_id) {
      rdAlert('Aucun compte Xtream trouvé pour ce client', 'warning');
      return;
    }

    rdConfirm(
      `Générer un nouveau mot de passe pour ${client.nom || client.xtream_username} ?\nL'ancien mot de passe sera inutilisable immédiatement.`,
      async () => {
        setClientBusy(client.id, 'password', true);
        try {
          const response = await axios.put(
            `${API_URL}/resellers/clients/${client.id}/xtream/reset-password`,
            {},
            { headers: resellerAuthHeaders() }
          );
          updateClient(client.id, () => ({ xtream_password: response.data.password }));
          rdAlert(`✅ Nouveau mot de passe généré\nUtilisateur : ${response.data.username}\nMot de passe : ${response.data.password}`, 'success');
        } catch (error) {
          rdAlert(error.response?.data?.error || 'Erreur lors de la génération du mot de passe', 'error');
        } finally {
          setClientBusy(client.id, 'password', false);
        }
      }
    );
  };

  const openConnectionModal = async (client) => {
    if (!client?.xtream_id) {
      rdAlert('Aucun compte Xtream trouvé pour ce client', 'warning');
      return;
    }

    setConnectionModal(client);
    setConnectionData(null);
    setConnectionLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/resellers/clients/${client.id}/xtream/connections`,
        { headers: resellerAuthHeaders() }
      );
      setConnectionData(response.data);
    } catch (error) {
      rdAlert(error.response?.data?.error || 'Erreur lors du chargement des connexions', 'error');
      setConnectionModal(null);
    } finally {
      setConnectionLoading(false);
    }
  };

  const openBlockCatModal = async (client) => {
    if (!client?.xtream_id) {
      rdAlert('Aucun compte Xtream trouvé pour ce client', 'warning');
      return;
    }

    setBlockCatModal(client);
    setBlockLoading(true);
    setCatSearch('');
    try {
      const headers = resellerAuthHeaders();
      const [blockedRes, catsRes] = await Promise.all([
        axios.get(`${API_URL}/resellers/clients/${client.id}/xtream/blocked-categories`, { headers }),
        axios.get(`${API_URL}/resellers/categories`, { headers })
      ]);
      setBlockedCats((blockedRes.data.blocked || []).map(formatCategoryKey));
      setAllCategories((catsRes.data.categories || []).filter(cat => cat.category_id && cat.category_name && cat.category_type));
    } catch (error) {
      rdAlert(error.response?.data?.error || 'Erreur lors du chargement des catégories', 'error');
      setBlockCatModal(null);
    } finally {
      setBlockLoading(false);
    }
  };

  const toggleBlockCategory = async (client, category) => {
    const categoryKey = formatCategoryKey(category);
    const isBlocked = blockedCats.includes(categoryKey);
    setClientBusy(client.id, 'categories', true);
    try {
      if (isBlocked) {
        const response = await axios.delete(
          `${API_URL}/resellers/clients/${client.id}/xtream/block-category/${encodeURIComponent(category.category_id)}?category_type=${category.category_type}`,
          { headers: resellerAuthHeaders() }
        );
        setBlockedCats(prev => prev.filter(key => key !== categoryKey));
        updateClientBlockedCount(client.id, response.data.count || 0);
      } else {
        const response = await axios.post(
          `${API_URL}/resellers/clients/${client.id}/xtream/block-category`,
          { category_id: category.category_id, category_type: category.category_type },
          { headers: resellerAuthHeaders() }
        );
        setBlockedCats(prev => [...new Set([...prev, categoryKey])]);
        updateClientBlockedCount(client.id, response.data.count || 0);
      }
    } catch (error) {
      rdAlert(error.response?.data?.error || 'Erreur lors de la mise à jour de la catégorie', 'error');
    } finally {
      setClientBusy(client.id, 'categories', false);
    }
  };

  const handleBlockAllCategories = async (filteredCategories) => {
    if (!blockCatModal) return;
    const toBlock = filteredCategories.filter(cat => !blockedCats.includes(formatCategoryKey(cat)));
    if (toBlock.length === 0) return;

    setClientBusy(blockCatModal.id, 'categories', true);
    try {
      await Promise.all(toBlock.map(cat => axios.post(
        `${API_URL}/resellers/clients/${blockCatModal.id}/xtream/block-category`,
        { category_id: cat.category_id, category_type: cat.category_type },
        { headers: resellerAuthHeaders() }
      )));
      const next = [...new Set([...blockedCats, ...toBlock.map(formatCategoryKey)])];
      setBlockedCats(next);
      updateClientBlockedCount(blockCatModal.id, next.length);
    } catch (error) {
      rdAlert(error.response?.data?.error || 'Erreur lors du blocage des catégories', 'error');
    } finally {
      setClientBusy(blockCatModal.id, 'categories', false);
    }
  };

  const handleUnblockAllCategories = async (filteredCategories) => {
    if (!blockCatModal) return;
    const toUnblock = filteredCategories.filter(cat => blockedCats.includes(formatCategoryKey(cat)));
    if (toUnblock.length === 0) return;

    setClientBusy(blockCatModal.id, 'categories', true);
    try {
      await Promise.all(toUnblock.map(cat => axios.delete(
        `${API_URL}/resellers/clients/${blockCatModal.id}/xtream/block-category/${encodeURIComponent(cat.category_id)}?category_type=${cat.category_type}`,
        { headers: resellerAuthHeaders() }
      )));
      const removedKeys = new Set(toUnblock.map(formatCategoryKey));
      const next = blockedCats.filter(key => !removedKeys.has(key));
      setBlockedCats(next);
      updateClientBlockedCount(blockCatModal.id, next.length);
    } catch (error) {
      rdAlert(error.response?.data?.error || 'Erreur lors du déblocage des catégories', 'error');
    } finally {
      setClientBusy(blockCatModal.id, 'categories', false);
    }
  };

  const handleMarkMessageRead = async (messageId) => {
    const target = messages.find(message => message.id === messageId);
    if (!target || target.is_read || target.read_at) return;

    try {
      const token = localStorage.getItem('resellerToken');
      await axios.post(`${API_URL}/resellers/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(prev => prev.map(message => (
        message.id === messageId
          ? { ...message, is_read: 1, read_at: new Date().toISOString() }
          : message
      )));
      setMessageUnreadCount(prev => Math.max(prev - 1, 0));
    } catch (error) {
      rdAlert(error.response?.data?.error || 'Erreur lors du marquage du message', 'error');
    }
  };

  const handleMarkAllMessagesRead = async () => {
    if (messageUnreadCount === 0) return;

    try {
      const token = localStorage.getItem('resellerToken');
      await axios.post(`${API_URL}/resellers/messages/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const readAt = new Date().toISOString();
      setMessages(prev => prev.map(message => ({ ...message, is_read: 1, read_at: message.read_at || readAt })));
      setMessageUnreadCount(0);
    } catch (error) {
      rdAlert(error.response?.data?.error || 'Erreur lors du marquage des messages', 'error');
    }
  };

  const handleDeleteClient = (client) => {
    if (!client?.id) return;

    rdConfirm(`Supprimer ${client.nom || 'ce compte'} ?\nCette action supprimera le client et ses accès Xtream/M3U de votre espace.`, async () => {
      setDeletingClientId(client.id);
      try {
        const token = localStorage.getItem('resellerToken');
        await axios.delete(`${API_URL}/resellers/clients/${client.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClients(prev => prev.filter(item => item.id !== client.id));
        rdAlert('✅ Compte supprimé avec succès', 'success');
        loadData();
      } catch (error) {
        rdAlert(error.response?.data?.error || 'Erreur lors de la suppression du compte', 'error');
      } finally {
        setDeletingClientId(null);
      }
    });
  };

  const handlePayMembership = async () => {
    setMembershipLoading(true);
    try {
      const token = localStorage.getItem('resellerToken');
      const res = await axios.post(`${API_URL}/resellers/membership/initiate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.paymentUrl) {
        window.open(res.data.paymentUrl, '_blank');
      }
    } catch (err) {
      rdAlert(err.response?.data?.error || 'Erreur lors de l\'initiation du paiement', 'error');
    } finally {
      setMembershipLoading(false);
    }
  };

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('resellerToken');
      const withdrawalData = {
        ...withdrawalForm,
        payment_method: withdrawalForm.withdraw_mode
      };
      await axios.post(`${API_URL}/resellers/withdraw`, withdrawalData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      rdAlert('✅ Retrait initié avec succès ! Vous recevrez votre paiement sous peu.', 'success');
      setWithdrawalForm({ amount: '', payment_method: 'momo', payment_details: '', withdraw_mode: 'mtn', country_code: 'CM' });
      loadData();
    } catch (error) {
      rdAlert(error.response?.data?.error || 'Erreur lors de la demande de retrait', 'error');
    }
  };

  // Agrégateurs Mobile Money disponibles pour la recharge (déduits du téléphone).
  const AGG_LABELS = { moneyfusion: { label: 'MoneyFusion', icon: '🟢' }, leekpay: { label: 'LeekPay', icon: '🔵' } };
  const depositPhone = String(depositForm.whatsappNumber || '').replace(/[\s-]/g, '');
  const depositCountry = depositPhone.startsWith('+')
    ? (countriesCatalog.find(c => depositPhone.startsWith(c.dialCode)) || {}).code || null
    : null;
  const depositAvailableAggregators = (() => {
    if (!aggregatorsConfig) return ['moneyfusion'];
    const enabled = ['moneyfusion', 'leekpay'].filter(n => aggregatorsConfig[n]?.enabled);
    if (!depositCountry) return enabled;
    const covering = enabled.filter(n => (aggregatorsConfig[n]?.countries || []).includes(depositCountry));
    return covering.length ? covering : enabled;
  })();
  const depositActiveAggregator = depositAvailableAggregators.includes(selectedAggregator)
    ? selectedAggregator
    : (depositAvailableAggregators[0] || null);

  const handleDepositSubmit = async (e) => {
    e.preventDefault();

    if (!depositForm.amount || depositForm.amount <= 0) {
      rdAlert('Veuillez entrer un montant valide', 'warning');
      return;
    }

    if (!depositForm.whatsappNumber || depositForm.whatsappNumber.length < 8) {
      rdAlert('Veuillez entrer un numéro WhatsApp valide avec indicatif', 'warning');
      return;
    }

    if (!depositActiveAggregator) {
      rdAlert('Aucun moyen Mobile Money disponible actuellement. Utilisez PayPal / Carte.', 'error');
      return;
    }

    rdConfirm(`Procéder au paiement de ${depositForm.amount} FCFA via ${AGG_LABELS[depositActiveAggregator]?.label || 'Mobile Money'} ?`, async () => {
      setSubmittingDeposit(true);
      try {
        const token = localStorage.getItem('resellerToken');
        const response = await axios.post(`${API_URL}/resellers/credit/initiate-payment`, {
          ...depositForm,
          aggregator: depositActiveAggregator,
          ...(depositCountry && { country: depositCountry })
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.payment_url) {
          window.location.href = response.data.payment_url;
        } else {
          throw new Error('URL de paiement non reçue');
        }
      } catch (error) {
        rdAlert(error.response?.data?.error || 'Erreur lors de l\'initiation du paiement', 'error');
        setSubmittingDeposit(false);
      }
    });
  };

  const handleCreditSubscriptionCreate = async (e) => {
    e.preventDefault();
    
    if (!creditSubForm.clientName) {
      rdAlert('Veuillez entrer le nom du client', 'warning');
      return;
    }

    if (!creditSubForm.whatsappNumber || creditSubForm.whatsappNumber.length < 8) {
      rdAlert('Veuillez entrer un numéro WhatsApp valide', 'warning');
      return;
    }

    const selectedPlan = CREDIT_PLANS[creditSubForm.plan];
    if (selectedPlan.price > creditBalance) {
      rdAlert(`Crédit insuffisant. Plan: ${selectedPlan.price} FCFA - Disponible: ${creditBalance} FCFA`, 'error');
      return;
    }

    rdConfirm(`Générer un abonnement ${selectedPlan.name} (${selectedPlan.price} FCFA) pour ${creditSubForm.clientName} ?`, async () => {
      setCreatingCreditSub(true);
      setGeneratedAccount(null);
      try {
        const token = localStorage.getItem('resellerToken');
        const response = await axios.post(`${API_URL}/resellers/credit/create-subscription`, creditSubForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGeneratedAccount(response.data);
        setCreditSubForm({ clientName: '', whatsappNumber: '', plan: '1month' });
        const headers = { Authorization: `Bearer ${token}` };
        loadCreditData(headers);
        loadData();
      } catch (error) {
        rdAlert(error.response?.data?.error || 'Erreur lors de la création de l\'abonnement', 'error');
      } finally {
        setCreatingCreditSub(false);
      }
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('resellerToken');
    localStorage.removeItem('resellerData');
    navigate('/reseller/login');
  };

  const copyPageUrl = () => {
    const url = `${window.location.origin}/reseller/${reseller.slug}`;
    navigator.clipboard.writeText(url);
    rdAlert('✅ Lien copié !', 'success');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return (
      <div className="reseller-dashboard">
        <div className="dashboard-main">
          <div className="loading">Chargement...</div>
        </div>
      </div>
    );
  }

  const renderMembershipWall = (feature) => (
    <div style={{
      background: 'linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)',
      border: '1px solid rgba(22,163,74,0.3)',
      borderRadius: '16px',
      padding: '48px 32px',
      textAlign: 'center',
      maxWidth: '520px',
      margin: '40px auto'
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔒</div>
      <h3 style={{ color: '#F1F5F9', fontSize: '1.3rem', fontWeight: '700', marginBottom: '10px' }}>
        Fonctionnalité verrouillée
      </h3>
      <p style={{ color: '#94A3B8', fontSize: '0.95rem', marginBottom: '8px' }}>
        <strong style={{ color: '#E2E8F0' }}>{feature}</strong> est réservé aux revendeurs avec une adhésion active.
      </p>
      <p style={{ color: '#64748B', fontSize: '0.85rem', marginBottom: '28px' }}>
        Activez votre adhésion pour débloquer : Codes DÉMO 24h et Système de Crédit.
      </p>
      <div style={{
        background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.2)',
        borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', display: 'inline-block'
      }}>
        <div style={{ color: '#94A3B8', fontSize: '0.8rem', marginBottom: '4px' }}>Frais d'adhésion</div>
        <div style={{ color: '#22C55E', fontSize: '1.8rem', fontWeight: '800' }}>
          {membershipStatus?.membership_total?.toLocaleString() || '...'} <span style={{ fontSize: '0.9rem' }}>FCFA</span>
        </div>
        <div style={{ color: '#64748B', fontSize: '0.75rem', marginTop: '4px' }}>
          {membershipStatus?.membership_price?.toLocaleString() || '...'} FCFA + 5% frais de traitement
        </div>
      </div>
      <br />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '14px' }}>
        <button type="button" className={`pm-btn ${membershipPayMethod === 'momo' ? 'pm-active' : ''}`}
          onClick={() => { setMembershipPayMethod('momo'); setMembershipCardDone(false); setPpError(''); }}
        >📱 Mobile Money</button>
        <button type="button" className={`pm-btn ${membershipPayMethod === 'card' ? 'pm-active' : ''}`}
          onClick={() => { setMembershipPayMethod('card'); setPpError(''); }}
        >PayPal / Carte</button>
      </div>
      {ppError && membershipPayMethod === 'card' && <p style={{ color: '#F87171', fontSize: '0.82rem', marginBottom: '10px' }}>{ppError}</p>}
      {membershipCardDone ? (
        <div style={{ background: '#16A34A22', border: '1px solid #16A34A', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
          <p style={{ color: '#22C55E', fontWeight: '700' }}>✅ Adhésion activée !</p>
        </div>
      ) : membershipPayMethod === 'momo' ? (
        <button onClick={handlePayMembership} disabled={membershipLoading} style={{
          background: 'linear-gradient(135deg, #16A34A, #22C55E)', border: 'none',
          borderRadius: '10px', padding: '14px 32px', color: '#fff', fontWeight: '700',
          fontSize: '1rem', cursor: membershipLoading ? 'not-allowed' : 'pointer',
          opacity: membershipLoading ? 0.7 : 1, boxShadow: '0 4px 20px rgba(22,163,74,0.3)'
        }}>
          {membershipLoading ? '⏳ Chargement...' : `💳 Payer l'adhésion — ${membershipStatus?.membership_total?.toLocaleString() || '...'} FCFA`}
        </button>
      ) : renderPaypalCheckout('membership')}
      {membershipCardDone ? (
        <div style={{ background: '#16A34A22', border: '1px solid #16A34A', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
          <p style={{ color: '#22C55E', fontWeight: '700' }}>✅ Adhésion activée !</p>
        </div>
      ) : membershipPayMethod === 'momo' ? (
        <button onClick={handlePayMembership} disabled={membershipLoading} style={{
          background: 'linear-gradient(135deg, #16A34A, #22C55E)', border: 'none',
          borderRadius: '10px', padding: '14px 32px', color: '#fff', fontWeight: '700',
          fontSize: '1rem', cursor: membershipLoading ? 'not-allowed' : 'pointer',
          opacity: membershipLoading ? 0.7 : 1, boxShadow: '0 4px 20px rgba(22,163,74,0.3)'
        }}>
          {membershipLoading ? '⏳ Chargement...' : `💳 Payer l'adhésion — ${membershipStatus?.membership_total?.toLocaleString() || '...'} FCFA`}
        </button>
      ) : renderPaypalCheckout('membership')}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="rd-section">
            <div className="rd-section-header">
              <div>
                <h2 className="rd-section-title">📊 Vue d'ensemble</h2>
                <p className="rd-section-sub">Bonjour, {reseller?.nom} 👋</p>
              </div>
              <a href={`/reseller/${reseller?.slug}`} target="_blank" rel="noopener noreferrer" className="rd-btn-outline">
                🛍️ Ma boutique
              </a>
            </div>

            {error && <div className="rd-error">❌ {error}</div>}

            {/* Lien boutique */}
            <div className="rd-link-card">
              <div className="rd-link-label">🔗 Votre boutique personnalisée</div>
              <div className="rd-link-row">
                <code className="rd-link-code">{window.location.origin}/reseller/{reseller?.slug}</code>
                <button onClick={copyPageUrl} className="rd-btn-sm">📋 Copier</button>
                <a href={`/reseller/${reseller?.slug}`} target="_blank" rel="noopener noreferrer" className="rd-btn-sm rd-btn-sm--green">👁️ Voir</a>
              </div>
            </div>

            {/* Statistiques */}
            <div className="rd-stats-grid">
              <div className="rd-stat-card">
                <div className="rd-stat-icon">👥</div>
                <div>
                  <div className="rd-stat-value">{stats?.totalClients || 0}</div>
                  <div className="rd-stat-label">Clients total</div>
                  <div className="rd-stat-detail">{stats?.activeClients || 0} actifs</div>
                </div>
              </div>
              <div className="rd-stat-card">
                <div className="rd-stat-icon">💰</div>
                <div>
                  <div className="rd-stat-value">{stats?.balance?.toLocaleString() || 0}</div>
                  <div className="rd-stat-label">Solde (FCFA)</div>
                  {withdrawals.filter(w => w.status === 'pending').length > 0 && (
                    <div className="rd-stat-detail" style={{color:'#f59e0b'}}>⏳ {withdrawals.filter(w => w.status === 'pending').reduce((s,w) => s + w.amount, 0).toLocaleString()} FCFA en attente</div>
                  )}
                  {withdrawals.filter(w => w.status === 'pending').length === 0 && (
                    <div className="rd-stat-detail">Disponible</div>
                  )}
                </div>
              </div>
              <div className="rd-stat-card">
                <div className="rd-stat-icon">💸</div>
                <div>
                  <div className="rd-stat-value">{stats?.totalCommissions?.toLocaleString() || 0}</div>
                  <div className="rd-stat-label">Commissions (FCFA)</div>
                  <div className="rd-stat-detail">Total cumulé</div>
                </div>
              </div>
              <div className="rd-stat-card">
                <div className="rd-stat-icon">📈</div>
                <div>
                  <div className="rd-stat-value">{stats?.monthlyCommissions?.toLocaleString() || 0}</div>
                  <div className="rd-stat-label">Ce mois (FCFA)</div>
                  <div className="rd-stat-detail">Commissions</div>
                </div>
              </div>
            </div>

            {/* Accès rapides */}
            <div className="rd-quick-actions">
              <div className="rd-quick-title">⚡ Accès rapides</div>
              <div className="rd-quick-grid">
                <button className="rd-quick-btn" onClick={() => setActiveTab('clients')}>👥 Mes clients</button>
                <button className="rd-quick-btn" onClick={() => setActiveTab('commissions')}>💸 Commissions</button>
                <button className="rd-quick-btn" onClick={() => setActiveTab('withdrawals')}>💰 Retraits</button>
                <button className="rd-quick-btn" onClick={() => { setActiveTab('messages'); loadMessages(); }}>
                  ✉️ Message{messageUnreadCount > 0 ? ` (${messageUnreadCount})` : ''}
                </button>
                {reseller?.credit_system_enabled === 1 && (
                  <button className="rd-quick-btn" onClick={() => setActiveTab('credit')}>💳 Crédit</button>
                )}
              </div>
            </div>
          </div>
        );

      case 'clients':
        return (
          <>
          {/* Bandeau adhésion si non activée */}
          {membershipStatus && !membershipStatus.membership_paid && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(234,88,12,0.1))',
              border: '1px solid rgba(245,158,11,0.35)',
              borderRadius: '12px',
              padding: '14px 20px',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '1.4rem' }}>⚠️</span>
                <div>
                  <div style={{ color: '#FCD34D', fontWeight: '700', fontSize: '0.95rem' }}>Adhésion requise</div>
                  <div style={{ color: '#94A3B8', fontSize: '0.82rem' }}>
                    Payez l'adhésion ({membershipStatus?.membership_total?.toLocaleString() || '...'} FCFA) pour débloquer : Codes DÉMO 24h et Système de Crédit.
                  </div>
                </div>
              </div>
              {/* Payment method toggle */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <button type="button" className={`pm-btn ${membershipPayMethod === 'momo' ? 'pm-active' : ''}`}
                  onClick={() => { setMembershipPayMethod('momo'); setMembershipCardDone(false); setPpError(''); }}>
                  📱 Mobile Money
                </button>
                <button type="button" className={`pm-btn ${membershipPayMethod === 'card' ? 'pm-active' : ''}`}
                  onClick={() => { setMembershipPayMethod('card'); setPpError(''); }}>
                  PayPal / Carte
                </button>
              </div>
              {ppError && membershipPayMethod === 'card' && <p style={{ color: '#F87171', fontSize: '0.82rem', marginBottom: '8px' }}>{ppError}</p>}
              {membershipCardDone ? (
                <div style={{ background: '#16A34A22', border: '1px solid #16A34A', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                  <p style={{ color: '#22C55E', fontWeight: '700', margin: 0 }}>✅ Adhésion activée !</p>
                </div>
              ) : membershipPayMethod === 'momo' ? (
                <button
                  onClick={handlePayMembership}
                  disabled={membershipLoading}
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B, #F97316)',
                    border: 'none', borderRadius: '8px', padding: '10px 20px',
                    color: '#0D0D0D', fontWeight: '700', fontSize: '0.9rem',
                    cursor: membershipLoading ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap', opacity: membershipLoading ? 0.7 : 1
                  }}
                >
                  {membershipLoading ? '⏳...' : `💳 Payer — ${membershipStatus?.membership_total?.toLocaleString() || '...'} FCFA`}
                </button>
              ) : renderPaypalCheckout('membership')}
            </div>
          )}
          <div className="rd-section">
            <div className="rd-section-header">
              <div>
                <h2 className="rd-section-title">👥 Mes Clients</h2>
                <p className="rd-section-sub">{clients.length} client(s) au total</p>
              </div>
              {reseller?.can_generate_24h_codes === 1 && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button className="rd-btn-outline" onClick={() => document.getElementById('form-demo').scrollIntoView({ behavior: 'smooth' })}>
                    ⚡ Code DÉMO 24h
                  </button>
                </div>
              )}
            </div>

            {/* Formulaire code démo 24h */}
            {reseller?.can_generate_24h_codes === 1 && (
              <div id="form-demo" className="rd-form-card rd-form-card--blue">
                <h3>⚡ Code DÉMO 24h <span className="rd-badge-free">GRATUIT</span></h3>
                {!membershipStatus?.membership_paid && (
                  <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '12px', color: '#FCD34D', fontSize: '0.85rem' }}>
                    🔒 Adhésion requise pour générer des codes. <button onClick={handlePayMembership} style={{ background: 'none', border: 'none', color: '#22C55E', cursor: 'pointer', textDecoration: 'underline', fontWeight: '600', padding: 0 }}>Activer l'adhésion →</button>
                  </div>
                )}
                <p className="rd-hint">Parfait pour faire tester à vos clients potentiels. Expire après 24h.</p>
                <form onSubmit={handleGenerate24hCode} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="rd-field" style={{ flex: 1, minWidth: '200px' }}>
                    <label>Nom du client *</label>
                    <input type="text" value={generate24hForm.clientName} onChange={(e) => setGenerate24hForm({...generate24hForm, clientName: e.target.value})} placeholder="Jean Dupont" required />
                  </div>
                  <button type="submit" className="rd-btn-outline" disabled={generating24h}>
                    {generating24h ? '⏳ Génération...' : '⚡ Générer'}
                  </button>
                </form>
              </div>
            )}

            {/* Grille de cartes clients */}
            {clients.length === 0 ? (
              <div className="rd-empty">
                <div className="rd-empty-icon">👥</div>
                <p>Aucun client pour le moment</p>
                <p className="rd-empty-sub">Partagez votre boutique pour recevoir des abonnements</p>
              </div>
            ) : (
              <div className="client-cards-grid">
                {clients.map((client) => {
                  const now = new Date();
                  const hasXtreamAccount = Boolean(client.xtream_id);
                  const isPaused = hasXtreamAccount && client.xtream_is_enabled === 0;
                  const subActive = client.last_subscription_end && new Date(client.last_subscription_end) > now;
                  const xtreamActive = !isPaused && client.xtream_expires_at && new Date(client.xtream_expires_at) > now;
                  const isActive = !isPaused && (subActive || xtreamActive);
                  const isTrial = !subActive && xtreamActive && client.xtream_is_trial;
                  const expiryDate = subActive
                    ? client.last_subscription_end
                    : (xtreamActive ? client.xtream_expires_at : (client.last_subscription_end || client.xtream_expires_at));
                  return (
                    <div key={client.id} className={`client-card ${isPaused ? 'client-card--paused' : isActive ? 'client-card--active' : 'client-card--expired'}`}>
                      {/* En-tête de carte */}
                      <div className="client-card-header">
                        <div className="client-card-avatar">
                          {client.nom?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="client-card-identity">
                          <div className="client-card-name">{client.nom}</div>
                          <div className="client-card-email">{client.email}</div>
                        </div>
                        <span className={`client-status-badge ${isPaused ? 'status-paused' : isActive ? 'status-active' : 'status-expired'}`}>
                          {isPaused ? '⏸ Pause' : isActive ? (isTrial ? '⏳ Essai' : '● Actif') : '○ Expiré'}
                        </span>
                      </div>

                      {/* Infos abonnement */}
                      <div className="client-card-body">
                        <div className="client-info-row">
                          <span className="client-info-label">📱 Tél.</span>
                          <span className="client-info-value">{client.telephone || 'N/A'}</span>
                        </div>
                        <div className="client-info-row">
                          <span className="client-info-label">📅 Expiration</span>
                          <span className={`client-info-value ${isPaused ? 'text-amber' : isActive ? 'text-green' : 'text-red'}`}>
                            {expiryDate ? new Date(expiryDate).toLocaleDateString('fr-FR') : 'N/A'}
                          </span>
                        </div>
                        <div className="client-info-row">
                          <span className="client-info-label">📦 Abonnements</span>
                          <span className="client-info-value">{client.subscriptions_count || 0}</span>
                        </div>
                        <div className="client-info-row client-info-row--device-limit">
                          <span className="client-info-label">📺 Appareils</span>
                          <span className="client-info-value client-device-limit-value">
                            {formatDeviceLimit(client.xtream_max_connections)}
                          </span>
                        </div>
                        {hasXtreamAccount && (
                          <div className="client-info-row">
                            <span className="client-info-label">🚫 Catégories</span>
                            <span className="client-info-value">
                              {client.xtream_blocked_categories_count || 0} bloquée(s)
                            </span>
                          </div>
                        )}

                        {/* Identifiants Xtream */}
                        {(client.xtream_username || client.xtream_password) && (
                          <div className="client-creds-block">
                            <div className="client-creds-title">🔐 Identifiants Xtream</div>
                            <div className="client-cred-row">
                              <span className="client-cred-label">Name</span>
                              <code className="client-cred-value">{XTREAM_APP_NAME}</code>
                              <button className="client-cred-copy" onClick={() => copyToClipboard(XTREAM_APP_NAME, 'Name')} title="Copier">📋</button>
                            </div>
                            {client.xtream_username && (
                              <div className="client-cred-row">
                                <span className="client-cred-label">Utilisateur</span>
                                <code className="client-cred-value">{client.xtream_username}</code>
                                <button className="client-cred-copy" onClick={() => copyToClipboard(client.xtream_username, 'Utilisateur')} title="Copier">📋</button>
                              </div>
                            )}
                            {client.xtream_password && (
                              <div className="client-cred-row">
                                <span className="client-cred-label">Mot de passe</span>
                                <code className="client-cred-value">{client.xtream_password}</code>
                                <button className="client-cred-copy" onClick={() => copyToClipboard(client.xtream_password, 'Mot de passe')} title="Copier">📋</button>
                              </div>
                            )}
                            <div className="client-cred-row">
                              <span className="client-cred-label">URL Xtream</span>
                              <code className="client-cred-value">{XTREAM_PUBLIC_URL}</code>
                              <button className="client-cred-copy" onClick={() => copyToClipboard(XTREAM_PUBLIC_URL, 'URL Xtream')} title="Copier">📋</button>
                            </div>
                            <div className="client-cred-row">
                              <span className="client-cred-label">Lien M3U</span>
                              <code className="client-cred-value">{getM3uUrl({
                                username: client.xtream_username,
                                password: client.xtream_password,
                                m3u_short: client.m3u_short,
                                short_token: client.xtream_short_token
                              })}</code>
                              <button className="client-cred-copy" onClick={() => copyToClipboard(getM3uUrl({
                                username: client.xtream_username,
                                password: client.xtream_password,
                                m3u_short: client.m3u_short,
                                short_token: client.xtream_short_token
                              }), 'Lien M3U')} title="Copier">📋</button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="client-card-actions">
                        {client.xtream_username && client.xtream_password && (
                          <button
                            className="client-action-btn client-action-copy"
                            onClick={() => copyToClipboard(formatXtreamAndM3u({
                              username: client.xtream_username,
                              password: client.xtream_password,
                              m3u_short: client.m3u_short,
                              short_token: client.xtream_short_token
                            }), 'Tous les identifiants')}
                            title="Copier tout"
                          >
                            📋 Copier
                          </button>
                        )}
                        {client.telephone && (
                          <button
                            className="client-action-btn client-action-whatsapp"
                            onClick={() => {
                              const message = messageTemplates.credentials.message(client);
                              window.open(`https://wa.me/${client.telephone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
                            }}
                            title="WhatsApp"
                          >
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" style={{ flexShrink: 0 }}>
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.304-1.654a11.882 11.882 0 005.713 1.456h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            WA
                          </button>
                        )}
                        <button
                          className="client-action-btn client-action-renew"
                          onClick={() => {
                            setRenewModal(client);
                            setRenewPlan('premium');
                            setRenewPromoData(null);
                            setRenewCardDone(false);
                          }}
                          title="Prolonger l'abonnement"
                        >
                          🔄 Prolonger
                        </button>
                        {hasXtreamAccount && (
                          <>
                            <button
                              className={`client-action-btn ${isPaused ? 'client-action-activate' : 'client-action-pause'}`}
                              onClick={() => handleToggleClientPause(client)}
                              disabled={isClientBusy(client.id, 'pause')}
                              title={isPaused ? 'Réactiver le compte Xtream' : 'Mettre le compte Xtream en pause'}
                            >
                              {isClientBusy(client.id, 'pause') ? '...' : isPaused ? '▶ Activer' : '⏸ Pause'}
                            </button>
                            <button
                              className="client-action-btn client-action-password"
                              onClick={() => handleResetClientPassword(client)}
                              disabled={isClientBusy(client.id, 'password')}
                              title="Générer un nouveau mot de passe"
                            >
                              {isClientBusy(client.id, 'password') ? '...' : '🔑 Nouveau MDP'}
                            </button>
                            <button
                              className="client-action-btn client-action-connections"
                              onClick={() => openConnectionModal(client)}
                              title="Voir les IP connectées et les stats de visionnage"
                            >
                              🌐 Connexions
                            </button>
                            <button
                              className="client-action-btn client-action-categories"
                              onClick={() => openBlockCatModal(client)}
                              disabled={isClientBusy(client.id, 'categories')}
                              title="Gérer les catégories bloquées"
                            >
                              🚫 Catégories {client.xtream_blocked_categories_count > 0 ? `(${client.xtream_blocked_categories_count})` : ''}
                            </button>
                          </>
                        )}
                        <button
                          className="client-action-btn client-action-delete"
                          onClick={() => handleDeleteClient(client)}
                          disabled={deletingClientId === client.id}
                          title="Supprimer le compte"
                        >
                          {deletingClientId === client.id ? '...' : 'Supprimer'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </>
        );

      case 'commissions':
        return (
          <>
            <div className="content-header">
              <h2 className="content-title">Mes Commissions</h2>
              <p className="content-subtitle">Historique de vos gains</p>
            </div>

            <div className="content-section">
              {commissions.length === 0 ? (
                <div className="empty-state">Aucune commission pour le moment</div>
              ) : (
                <>
                  <div className="table-scroll-hint" style={{ display: 'none' }}>
                    ↔️ Faites défiler horizontalement pour voir toutes les colonnes
                  </div>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Client</th>
                      <th>Montant vente</th>
                      <th>Commission</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((commission) => (
                      <tr key={commission.id}>
                        <td>{new Date(commission.created_at).toLocaleDateString()}</td>
                        <td>{commission.user_email || 'N/A'}</td>
                        <td>{commission.amount?.toLocaleString() || 0} FCFA</td>
                        <td>{commission.commission_amount?.toLocaleString() || 0} FCFA</td>
                        <td>
                          <span className={`status-badge status-${commission.status || 'pending'}`}>
                            {commission.status || 'pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
                </>
              )}
            </div>
          </>
        );

      case 'withdrawals':
        return (
          <>
            <div className="content-header">
              <h2 className="content-title">Retraits</h2>
              <p className="content-subtitle">Gérez vos demandes de retrait</p>
            </div>

            <div className="content-section">
              <h3 className="section-title">Demande de retrait</h3>
              <form onSubmit={handleWithdrawal} className="withdraw-form">
                <div className="form-group">
                  <label className="form-label">Sélectionner pays</label>
                  <select
                    className="form-select"
                    value={withdrawalForm.country_code}
                    onChange={(e) => {
                      const country = PAYOUT_COUNTRIES.find(c => c.code === e.target.value);
                      setWithdrawalForm({
                        ...withdrawalForm,
                        country_code: e.target.value,
                        withdraw_mode: country?.defaultMode || ''
                      });
                    }}
                  >
                    {PAYOUT_COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Sélectionner opérateur</label>
                  <select
                    className="form-select"
                    value={withdrawalForm.withdraw_mode}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, withdraw_mode: e.target.value })}
                  >
                    {(COUNTRY_OPERATORS[withdrawalForm.country_code] || []).map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Numéro Mobile Money (avec indicatif pays)</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={withdrawalForm.payment_details}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, payment_details: e.target.value })}
                    placeholder="ex: 237690000000"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Montant à retirer (FCFA)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={withdrawalForm.amount}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })}
                    required
                    min="1000"
                  />
                  {withdrawalForm.amount > 0 && (
                    <small style={{ color: '#9ca3af', marginTop: '4px', display: 'block' }}>
                      Frais 5%: {Math.round(withdrawalForm.amount * 0.05).toLocaleString()} FCFA → Net reçu: {Math.round(withdrawalForm.amount * 0.95).toLocaleString()} FCFA
                    </small>
                  )}
                </div>

                <button type="submit" className="withdraw-btn" disabled={stats?.balance < 1000}>
                  💸 Demander un retrait
                </button>
              </form>
            </div>

            <div className="content-section">
              <h3 className="section-title">Historique des retraits</h3>
              {withdrawals.length === 0 ? (
                <div className="empty-state">Aucun retrait pour le moment</div>
              ) : (
                <>
                  <div className="table-scroll-hint" style={{ display: 'none' }}>
                    ↔️ Faites défiler horizontalement pour voir toutes les colonnes
                  </div>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Demandé</th>
                      <th>Net reçu</th>
                      <th>Méthode</th>
                      <th>Statut</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map((withdrawal) => (
                      <tr key={withdrawal.id}>
                        <td>{new Date(withdrawal.requested_at).toLocaleDateString()}</td>
                        <td>{withdrawal.amount?.toLocaleString()} FCFA</td>
                        <td>{withdrawal.net_amount?.toLocaleString()} FCFA</td>
                        <td>{withdrawal.payment_method}</td>
                        <td>
                          <span className={`status-badge status-${withdrawal.status}`}>
                            {withdrawal.status === 'pending' ? '⏳ En attente' : withdrawal.status === 'approved' ? '✅ Approuvé' : withdrawal.status === 'rejected' ? '❌ Rejeté' : withdrawal.status}
                          </span>
                        </td>
                        <td style={{fontSize:'0.85em',color:'#9ca3af'}}>{withdrawal.admin_note || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
                </>
              )}
            </div>
          </>
        );

      case 'messages':
        return (
          <>
            <div className="content-header reseller-messages-header">
              <div>
                <h2 className="content-title">Message</h2>
                <p className="content-subtitle">
                  {messageUnreadCount > 0
                    ? `${messageUnreadCount} message${messageUnreadCount > 1 ? 's' : ''} non lu${messageUnreadCount > 1 ? 's' : ''}`
                    : 'Tous les messages sont lus'}
                </p>
              </div>
              {messageUnreadCount > 0 && (
                <button type="button" className="rd-btn-outline" onClick={handleMarkAllMessagesRead}>
                  Tout marquer comme lu
                </button>
              )}
            </div>

            <div className="content-section reseller-panel-messages">
              {messages.length === 0 ? (
                <div className="empty-state">Aucun message pour le moment</div>
              ) : (
                <div className="reseller-panel-message-list">
                  {messages.map((message) => {
                    const isRead = message.is_read || message.read_at;
                    return (
                      <article key={message.id} className={`reseller-panel-message ${isRead ? '' : 'unread'} type-${message.type || 'info'}`}>
                        <div className="reseller-panel-message-head">
                          <div>
                            <div className="reseller-panel-message-title">
                              {!isRead && <span className="unread-dot" aria-hidden="true"></span>}
                              {message.title}
                            </div>
                            <div className="reseller-panel-message-date">
                              {new Date(message.created_at).toLocaleString('fr-FR')}
                            </div>
                          </div>
                          <span className={`reseller-panel-message-type type-${message.type || 'info'}`}>
                            {message.type === 'success' ? 'Succès' : message.type === 'warning' ? 'Important' : message.type === 'error' ? 'Urgent' : 'Info'}
                          </span>
                        </div>
                        <p>{message.message}</p>
                        {!isRead && (
                          <button type="button" className="rd-btn-sm" onClick={() => handleMarkMessageRead(message.id)}>
                            Marquer comme lu
                          </button>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        );

      case 'credit':
        return !membershipStatus?.membership_paid
          ? renderMembershipWall("Système de Crédit")
          : (
          <>
            <div className="content-header">
              <h2 className="content-title">Système de Crédit</h2>
              <p className="content-subtitle">Gérez votre crédit et créez des abonnements</p>
            </div>

            {/* Solde de crédit */}
            <div className="credit-balance-card">
              <div className="label">Solde de crédit disponible</div>
              <div className="amount">{creditBalance.toLocaleString()} FCFA</div>
            </div>

            <div style={{ display: 'grid', gap: '2rem', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))' }}>
              {/* Formulaire de dépôt */}
              <div className="content-section">
                <h3 className="section-title">💳 Recharger mon crédit</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                  Rechargez votre compte pour créer des abonnements
                </p>
                {/* Payment method toggle */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
                  <button type="button" className={`pm-btn ${depositPayMethod === 'momo' ? 'pm-active' : ''}`}
                    onClick={() => { setDepositPayMethod('momo'); setDepositCardDone(false); setPpError(''); }}>
                    📱 Mobile Money
                  </button>
                  <button type="button" className={`pm-btn ${depositPayMethod === 'card' ? 'pm-active' : ''}`}
                    onClick={() => { setDepositPayMethod('card'); setPpError(''); }}>
                    PayPal / Carte
                  </button>
                </div>
                {ppError && <p style={{ color: '#F87171', fontSize: '0.82rem', marginBottom: '10px' }}>{ppError}</p>}
                {depositCardDone ? (
                  <div style={{ background: '#16A34A22', border: '1px solid #16A34A', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                    <p style={{ color: '#22C55E', fontWeight: '700' }}>✅ Crédit ajouté avec succès !</p>
                    <button className="btn-secondary" onClick={() => setDepositCardDone(false)}>Nouveau dépôt</button>
                  </div>
                ) : depositPayMethod === 'momo' ? (
                  <form onSubmit={handleDepositSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">Montant à déposer (FCFA) *</label>
                      <input
                        type="number"
                        className="form-input"
                        value={depositForm.amount}
                        onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                        placeholder="Ex: 10000"
                        required
                        min="500"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Numéro WhatsApp (avec indicatif) *</label>
                      <input
                        type="tel"
                        className="form-input"
                        value={depositForm.whatsappNumber}
                        onChange={(e) => setDepositForm({ ...depositForm, whatsappNumber: e.target.value })}
                        placeholder="Ex: +22890123456"
                        required
                      />
                      <small style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                        Format: +indicatif + numéro (ex: +228 pour Togo)
                      </small>
                    </div>
                    {depositAvailableAggregators.length > 1 && (
                      <div className="form-group">
                        <label className="form-label">Service de paiement</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {depositAvailableAggregators.map(name => (
                            <button key={name} type="button"
                              className={`pm-btn ${depositActiveAggregator === name ? 'pm-active' : ''}`}
                              style={{ flex: '1 1 130px' }}
                              onClick={() => setSelectedAggregator(name)}>
                              {AGG_LABELS[name]?.icon} {AGG_LABELS[name]?.label || name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {depositAvailableAggregators.length === 0 && (
                      <p style={{ color: '#F87171', fontSize: '0.85rem' }}>
                        Aucun moyen Mobile Money disponible actuellement. Utilisez PayPal / Carte.
                      </p>
                    )}
                    <button type="submit" className="withdraw-btn" disabled={submittingDeposit || depositAvailableAggregators.length === 0} style={{ marginTop: '0.5rem' }}>
                      {submittingDeposit ? 'Redirection...' : `💳 Dépôt via ${AGG_LABELS[depositActiveAggregator]?.label || 'Mobile Money'}`}
                    </button>
                  </form>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">Montant à déposer (FCFA) *</label>
                      <input
                        type="number"
                        className="form-input"
                        value={depositForm.amount}
                        onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                        placeholder="Ex: 10000"
                        min="500"
                      />
                      <small style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                        Minimum 500 FCFA. Le montant USD exact est affiché par PayPal.
                      </small>
                    </div>
                    {depositForm.amount && Number(depositForm.amount) >= 500 ? (
                      renderPaypalCheckout('deposit', { amount: Number(depositForm.amount) })
                    ) : (
                      <p style={{ color: '#94A3B8', fontSize: '0.85rem' }}>Saisissez un montant pour continuer.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Formulaire création abonnement avec crédit */}
              <div className="content-section">
                <h3 className="section-title">✨ Générer un code Xtream</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                  Créez un abonnement instantanément avec votre crédit
                </p>
                <form onSubmit={handleCreditSubscriptionCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Nom du client *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={creditSubForm.clientName}
                      onChange={(e) => setCreditSubForm({ ...creditSubForm, clientName: e.target.value })}
                      placeholder="Ex: Jean Dupont"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Numéro WhatsApp du client *</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={creditSubForm.whatsappNumber}
                      onChange={(e) => setCreditSubForm({ ...creditSubForm, whatsappNumber: e.target.value })}
                      placeholder="Ex: +22890123456"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Choisir un plan *</label>
                    <select
                      className="form-select"
                      value={creditSubForm.plan}
                      onChange={(e) => setCreditSubForm({ ...creditSubForm, plan: e.target.value })}
                    >
                      {Object.entries(CREDIT_PLANS).map(([key, plan]) => (
                        <option key={key} value={key}>
                          {plan.name} - {plan.price.toLocaleString()} FCFA
                        </option>
                      ))}
                    </select>
                    <small style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                      Solde disponible: {creditBalance.toLocaleString()} FCFA
                    </small>
                  </div>

                  <button 
                    type="submit" 
                    className="btn-generate-credit"
                    disabled={creatingCreditSub || creditBalance < CREDIT_PLANS[creditSubForm.plan].price}
                  >
                    {creatingCreditSub ? '⏳ Génération en cours...' : '🎯 Générer le code Xtream'}
                  </button>
                </form>

                {/* Affichage du compte généré */}
                {generatedAccount && (
                  <div className="generated-account-box">
                    <h4>✅ Code Xtream généré avec succès !</h4>

                    <div className="generated-account-field">
                      <div className="generated-account-field-header">
                        <strong>Name:</strong>
                        <button
                          className="generated-copy-btn"
                          onClick={() => copyToClipboard(XTREAM_APP_NAME, 'Name')}
                        >
                          📋 Copier
                        </button>
                      </div>
                      <div className="mono">{XTREAM_APP_NAME}</div>
                    </div>
                    
                    <div className="generated-account-field">
                      <div className="generated-account-field-header">
                        <strong>Utilisateur:</strong>
                        <button 
                          className="generated-copy-btn"
                          onClick={() => copyToClipboard(generatedAccount.username, 'Utilisateur')}
                        >
                          📋 Copier
                        </button>
                      </div>
                      <div className="mono">{generatedAccount.username}</div>
                    </div>

                    <div className="generated-account-field">
                      <div className="generated-account-field-header">
                        <strong>Mot de passe:</strong>
                        <button 
                          className="generated-copy-btn"
                          onClick={() => copyToClipboard(generatedAccount.password, 'Mot de passe')}
                        >
                          📋 Copier
                        </button>
                      </div>
                      <div className="mono">{generatedAccount.password}</div>
                    </div>

                    <div className="generated-account-field">
                      <div className="generated-account-field-header">
                        <strong>URL Xtream:</strong>
                        <button 
                          className="generated-copy-btn"
                          onClick={() => copyToClipboard(XTREAM_PUBLIC_URL, 'URL Xtream')}
                        >
                          📋 Copier
                        </button>
                      </div>
                      <div className="mono">{XTREAM_PUBLIC_URL}</div>
                    </div>

                    <div className="generated-account-field">
                      <div className="generated-account-field-header">
                        <strong>Lien M3U:</strong>
                        <button
                          className="generated-copy-btn"
                          onClick={() => copyToClipboard(getM3uUrl(generatedAccount), 'Lien M3U')}
                        >
                          📋 Copier
                        </button>
                      </div>
                      <div className="mono">{getM3uUrl(generatedAccount)}</div>
                    </div>

                    <button
                      className="generated-copy-btn"
                      onClick={() => copyToClipboard(formatXtreamAndM3u({
                        username: generatedAccount.username,
                        password: generatedAccount.password,
                        m3u_short: generatedAccount.m3u_short,
                        m3uUrl: generatedAccount.m3uUrl,
                        short_token: generatedAccount.short_token
                      }), 'Tous les identifiants')}
                      style={{ marginBottom: '12px' }}
                    >
                      📋 Copier tous les identifiants
                    </button>

                    <div className="generated-account-meta">
                      <div>👤 Client: {generatedAccount.client_name}</div>
                      <div>📱 WhatsApp: {generatedAccount.whatsapp}</div>
                      <div>📅 Expire le: {new Date(generatedAccount.expires_at).toLocaleDateString('fr-FR')}</div>
                      <div>💰 Coût: {generatedAccount.credits_used} FCFA</div>
                      <div>💳 Nouveau solde: {generatedAccount.new_balance.toLocaleString()} FCFA</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Historique des dépôts */}
            <div className="content-section" style={{ marginTop: '2rem' }}>
              <h3 className="section-title">📋 Historique des dépôts</h3>
              {deposits.length === 0 ? (
                <div className="empty-state">Aucun dépôt pour le moment</div>
              ) : (
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Montant</th>
                        <th>Méthode</th>
                        <th>Référence</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deposits.map((deposit) => (
                        <tr key={deposit.id}>
                          <td>{new Date(deposit.created_at).toLocaleDateString()}</td>
                          <td>{deposit.amount?.toLocaleString()} FCFA</td>
                          <td>{deposit.payment_method}</td>
                          <td>{deposit.payment_reference || '-'}</td>
                          <td>
                            <span className={`status-badge status-${deposit.status}`}>
                              {deposit.status === 'pending' ? 'En attente' : deposit.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Historique des transactions */}
            <div className="content-section" style={{ marginTop: '2rem' }}>
              <h3 className="section-title">💸 Transactions de crédit</h3>
              {creditTransactions.length === 0 ? (
                <div className="empty-state">Aucune transaction pour le moment</div>
              ) : (
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Montant</th>
                        <th>Solde après</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditTransactions.map((transaction, index) => (
                        <tr key={index}>
                          <td>{new Date(transaction.created_at).toLocaleDateString()}</td>
                          <td>
                            <span className={transaction.type === 'credit' ? 'tx-credit' : 'tx-debit'}>
                              {transaction.type === 'credit' ? '➕ Crédit' : '➖ Débit'}
                            </span>
                          </td>
                          <td>{transaction.description}</td>
                          <td>{transaction.amount?.toLocaleString()} FCFA</td>
                          <td>{transaction.balance_after?.toLocaleString()} FCFA</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
          );

      default:
        return null;
    }
  };

  return (
    <div className="reseller-dashboard">
      {/* Navbar - Nouveau Design */}
      <nav className="new-design-navbar" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001 }}>
        <div className="new-design-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <a href="/reseller/dashboard" className="new-design-logo">
            <img src="/logo.png" alt="Fluxivo" className="new-design-logo-img" style={{height: '36px', width: 'auto'}} />
            <span className="new-design-logo-text">Flux<span>ivo</span></span>
          </a>
          
          {/* Liens desktop uniquement */}
          <ul className="new-design-nav-links" style={{ margin: 0 }}>
            <li><a href={`/reseller/${reseller?.slug}`} target="_blank" rel="noopener noreferrer">🛍️ Ma boutique</a></li>
            <li style={{ color: 'var(--text-secondary)' }}>👤 {reseller?.nom}</li>
          </ul>
          
          {/* Info mobile - visible seulement sur petit écran */}
          <div className="mobile-user-info">
            👤 {reseller?.nom}
          </div>
          
          <button onClick={handleLogout} className="new-design-btn-login">
            Déconnexion
          </button>
        </div>
      </nav>

      {/* Mobile menu toggle */}
      <button 
        className={`mobile-menu-toggle ${sidebarOpen ? 'active' : ''}`} 
        onClick={toggleSidebar}
        style={{ position: 'fixed', top: '80px', left: '10px', zIndex: 1002 }}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Sidebar overlay for mobile */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={toggleSidebar}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 999,
          display: sidebarOpen ? 'block' : 'none'
        }}
      />

      {/* Sidebar */}
      <div className={`rd-sidebar ${sidebarOpen ? 'rd-sidebar--open' : ''}`}>
        <div className="rd-sidebar-balance">
          <div className="rd-sidebar-balance-label">Solde disponible</div>
          <div className="rd-sidebar-balance-amount">{stats?.balance?.toLocaleString() || 0} <span>FCFA</span></div>
        </div>
        {/* Statut adhésion */}
        {membershipStatus && (
          <div style={{
            margin: '0 12px 12px',
            padding: '8px 12px',
            borderRadius: '8px',
            background: membershipStatus.membership_paid ? 'rgba(22,163,74,0.12)' : 'rgba(245,158,11,0.1)',
            border: `1px solid ${membershipStatus.membership_paid ? 'rgba(22,163,74,0.3)' : 'rgba(245,158,11,0.3)'}`,
            display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem'
          }}>
            <span>{membershipStatus.membership_paid ? '✅' : '🔒'}</span>
            <span style={{ color: membershipStatus.membership_paid ? '#22C55E' : '#FCD34D', fontWeight: '600' }}>
              {membershipStatus.membership_paid ? 'Adhésion active' : 'Adhésion requise'}
            </span>
          </div>
        )}
        <nav className="rd-sidebar-nav">
          {[
            { key: 'overview', icon: '📊', label: 'Vue d\'ensemble' },
            { key: 'clients',  icon: '👥', label: 'Mes Clients' },
            { key: 'commissions', icon: '💸', label: 'Commissions' },
            { key: 'withdrawals', icon: '💰', label: 'Retraits' },
            { key: 'messages', icon: '✉️', label: 'Message', badge: messageUnreadCount },
            ...(reseller?.credit_system_enabled === 1 ? [{ key: 'credit', icon: '💳', label: 'Crédit' }] : [])
          ].map(({ key, icon, label, badge }) => (
            <button
              key={key}
              className={`rd-nav-item ${activeTab === key ? 'rd-nav-item--active' : ''}`}
              onClick={() => {
                setActiveTab(key);
                setSidebarOpen(false);
                if (key === 'messages') loadMessages();
              }}
            >
              <span className="rd-nav-icon">{icon}</span>
              <span className="rd-nav-label">{label}</span>
              {badge > 0 && <span className="rd-nav-badge">{badge > 99 ? '99+' : badge}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Main content */}
      <div className="rd-main">
        <div className="rd-content">
          {renderContent()}
        </div>
      </div>

      {/* Modal pour afficher le code 24h généré */}
      {generated24hCode && (
        <div 
          className="modal-fullscreen" 
          onClick={closeGenerated24hModal}
        >
          <div 
            className="modal-box-green" 
            onClick={(e) => e.stopPropagation()}
          >
            <h2>
              <span>✅</span> Code DÉMO 24h Généré !
            </h2>
            <p className="subtitle">
              Pour {generated24hCode.client_name}
            </p>

            <div className="modal-fields-block">
              <div className="modal-field">
                <label>Name</label>
                <div className="modal-field-row">
                  <input
                    type="text"
                    value={XTREAM_APP_NAME}
                    readOnly
                  />
                  <button
                    className="modal-copy-btn"
                    onClick={() => copyToClipboard(XTREAM_APP_NAME, 'Name')}
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="modal-field">
                <label>URL Xtream</label>
                <div className="modal-field-row">
                  <input 
                    type="text" 
                    value={XTREAM_PUBLIC_URL}
                    readOnly
                  />
                  <button
                    className="modal-copy-btn"
                    onClick={() => copyToClipboard(`${XTREAM_PUBLIC_URL}\n⏰ Expire: ${new Date(generated24hCode.expires_at).toLocaleString('fr-FR')}`, 'URL Xtream')}
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="modal-field">
                <label>Utilisateur</label>
                <div className="modal-field-row">
                  <input 
                    type="text" 
                    value={generated24hCode.username}
                    readOnly
                    className="highlight"
                  />
                  <button
                    className="modal-copy-btn"
                    onClick={() => copyToClipboard(`${generated24hCode.username}\n⏰ Expire: ${new Date(generated24hCode.expires_at).toLocaleString('fr-FR')}`, 'Utilisateur')}
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="modal-field">
                <label>Mot de passe</label>
                <div className="modal-field-row">
                  <input 
                    type="text" 
                    value={generated24hCode.password}
                    readOnly
                    className="highlight"
                  />
                  <button
                    className="modal-copy-btn"
                    onClick={() => copyToClipboard(`${generated24hCode.password}\n⏰ Expire: ${new Date(generated24hCode.expires_at).toLocaleString('fr-FR')}`, 'Mot de passe')}
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="modal-field">
                <label>Lien M3U</label>
                <div className="modal-field-row">
                  <input
                    type="text"
                    value={getM3uUrl(generated24hCode)}
                    readOnly
                  />
                  <button
                    className="modal-copy-btn"
                    onClick={() => copyToClipboard(`${getM3uUrl(generated24hCode)}\n⏰ Expire: ${new Date(generated24hCode.expires_at).toLocaleString('fr-FR')}`, 'Lien M3U')}
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="modal-expiry-notice">
                ⏰ <strong>Expire le:</strong> {new Date(generated24hCode.expires_at).toLocaleString('fr-FR')}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-generate-main"
                onClick={() => {
                  const text = `🎉 Voici votre code DÉMO Fluxivo 24h:\n\n${formatXtreamAndM3u({
                    username: generated24hCode.username,
                    password: generated24hCode.password,
                    m3u_short: generated24hCode.m3u_short,
                    m3uUrl: generated24hCode.m3uUrl,
                    short_token: generated24hCode.short_token
                  })}\n\n⏰ Expire: ${new Date(generated24hCode.expires_at).toLocaleString('fr-FR')}\n\nTestez et profitez ! 📺`;
                  copyToClipboard(text, 'Toutes les informations');
                }}
              >
                📋 Copier tout
              </button>
              <button
                className="btn-modal-close"
                onClick={closeGenerated24hModal}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bannière de succès après prolongement */}
      {renewalBanner && (
        <div className="renewal-success-banner" onClick={() => setRenewalBanner(null)}>
          <div className="renewal-success-box" onClick={(e) => e.stopPropagation()}>
            <div className="renewal-success-icon">✅</div>
            <h3>Abonnement prolongé !</h3>
            <p className="renewal-client-name">{renewalBanner.clientName}</p>
            {renewalBanner.expiryDate && (
              <p className="renewal-expiry">
                📅 Nouvel expiration :{' '}
                <strong>{new Date(renewalBanner.expiryDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
              </p>
            )}
            {renewalBanner.xtreamUsername && (
              <div className="renewal-creds">
                <div>👤 <strong>{renewalBanner.xtreamUsername}</strong></div>
                <div>🔑 <strong>{renewalBanner.xtreamPassword}</strong></div>
              </div>
            )}
            <button className="btn-modal-close" onClick={() => setRenewalBanner(null)}>Fermer</button>
          </div>
        </div>
      )}

      {/* Modal prolongement d'abonnement */}
      {renewModal && (
        <div className="modal-fullscreen" onClick={() => { setRenewModal(null); setRenewPromoData(null); }}>
          <div className="modal-box-green" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <h2>🔄 Prolonger l'abonnement</h2>
            <p className="subtitle">Client : <strong>{renewModal.nom}</strong></p>
            <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '20px' }}>{renewModal.email}</p>

            <div style={{ marginBottom: '20px' }}>
              <label className="form-label-inline">Choisir le plan</label>
              <div className="renew-plan-grid">
                {Object.entries(resellerPlans).map(([key, plan]) => (
                  <div
                    key={key}
                    className={`renew-plan-card ${renewPlan === key ? 'selected' : ''}`}
                    onClick={() => { setRenewPlan(key); setRenewCardDone(false); }}
                  >
                    <div className="renew-plan-name">{plan.name.replace(' - ', '\n')}</div>
                    <div className="renew-plan-price">{plan.price.toLocaleString()} FCFA</div>
                    <div className="renew-plan-duration">{plan.duration} jours</div>
                    <div className="renew-plan-connections">📱 {plan.connections} appareil{plan.connections > 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <PromoCodeInput
                key={renewPlan}
                planType={renewPlan}
                planPrice={resellerPlans[renewPlan]?.price || 0}
                onPromoApplied={setRenewPromoData}
              />
            </div>

            {/* Méthode de paiement */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <button type="button"
                className={`pm-btn ${renewPayMethod === 'momo' ? 'pm-active' : ''}`}
                onClick={() => { setRenewPayMethod('momo'); setRenewCardDone(false); setPpError(''); }}
              >📱 Mobile Money</button>
              <button type="button"
                className={`pm-btn ${renewPayMethod === 'card' ? 'pm-active' : ''}`}
                onClick={() => { setRenewPayMethod('card'); setPpError(''); }}
              >PayPal / Carte</button>
              {creditEnabled && (
                <button type="button"
                  className={`pm-btn ${renewPayMethod === 'credit' ? 'pm-active' : ''}`}
                  onClick={() => { setRenewPayMethod('credit'); setRenewCardDone(false); setPpError(''); }}
                >🏦 Crédit</button>
              )}
            </div>
            {renewPayMethod === 'credit' && (
              <p style={{ color: creditBalance >= (renewPromoData?.finalPrice ?? resellerPlans[renewPlan]?.price) ? '#22C55E' : '#F87171', fontSize: '0.82rem', marginBottom: '10px' }}>
                Solde crédit: {creditBalance.toLocaleString()} FCFA
                {renewPromoData?.code && (
                  <span> - Après promo: {(renewPromoData.finalPrice ?? resellerPlans[renewPlan]?.price).toLocaleString()} FCFA</span>
                )}
              </p>
            )}

            {ppError && <p style={{ color: '#F87171', fontSize: '0.82rem', marginBottom: '10px' }}>{ppError}</p>}

            {renewCardDone ? (
              <div style={{ background: '#16A34A22', border: '1px solid #16A34A', borderRadius: '10px', padding: '16px', textAlign: 'center', marginBottom: '12px' }}>
                <p style={{ color: '#22C55E', fontWeight: '700' }}>✅ Prolongement activé !</p>
              </div>
            ) : (
              <div className="modal-actions">
                {renewPayMethod === 'momo' ? (
                  <button className="btn-generate-main" onClick={handleRenewal} disabled={renewLoading}>
                    {renewLoading ? '⏳ Chargement...' : 'Procéder au paiement'}
                  </button>
                ) : renewPayMethod === 'credit' ? (
                  <button
                    className="btn-generate-main"
                    onClick={handleCreditRenewal}
                    disabled={renewLoading || creditBalance < (renewPromoData?.finalPrice ?? resellerPlans[renewPlan]?.price)}
                  >
                    {renewLoading ? '⏳ Prolongement...' : 'Procéder au paiement'}
                  </button>
                ) : renderPaypalCheckout('renewal', {
                  plan_type: renewPlan, promo_code: renewPromoData?.code,
                  client_email: renewModal.email, client_phone: renewModal.telephone
                })}
                <button className="btn-modal-close" onClick={() => { setRenewModal(null); setRenewPromoData(null); setRenewCardDone(false); }}>
                  Annuler
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal connexions et visionnage */}
      {connectionModal && (
        <div className="modal-fullscreen" onClick={() => setConnectionModal(null)}>
          <div className="modal-box-green reseller-connection-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🌐 Connexions client</h2>
            <p className="subtitle">
              Client : <strong>{connectionModal.nom || connectionModal.xtream_username}</strong>
              {connectionData?.username && <> — {connectionData.username}</>}
            </p>

            {connectionLoading ? (
              <div className="category-list-loading">Chargement des connexions...</div>
            ) : connectionData ? (
              <>
                <div className="connection-stats-grid">
                  {[
                    { label: 'Actifs', value: `${connectionData.active_devices || 0}/${connectionData.max_connections || 1}` },
                    { label: "Aujourd'hui", value: connectionData.stats?.today_sessions || 0 },
                    { label: '7 jours', value: connectionData.stats?.week_sessions || 0 },
                    { label: 'IP vues', value: connectionData.stats?.unique_ips || 0 },
                  ].map(item => (
                    <div className="connection-stat" key={item.label}>
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="connection-type-row">
                  <span>Live: <strong>{connectionData.stats?.live_sessions || 0}</strong></span>
                  <span>Films: <strong>{connectionData.stats?.vod_sessions || 0}</strong></span>
                  <span>Séries: <strong>{connectionData.stats?.series_sessions || 0}</strong></span>
                </div>

                <div className="connection-section">
                  <h3>IP connectées maintenant</h3>
                  {(connectionData.active_connections || []).length === 0 ? (
                    <div className="connection-empty">Aucune connexion active détectée.</div>
                  ) : (
                    <div className="connection-list">
                      {(connectionData.active_connections || []).map((conn) => (
                        <div className="connection-row" key={conn.ip_address}>
                          <div className="connection-row-main">
                            <strong>{conn.ip_address || 'IP inconnue'}</strong>
                            <span>{conn.active_streams || 0} flux actif(s) · dernière activité {formatDateTime(conn.last_activity)}</span>
                            {conn.user_agent && <small title={conn.user_agent}>{conn.user_agent}</small>}
                          </div>
                          <div className="connection-streams">
                            {(conn.streams || []).length === 0 ? (
                              <span className="connection-pill">Flux actif</span>
                            ) : (
                              conn.streams.map((stream, index) => (
                                <span className="connection-pill" key={`${stream.stream_id}-${index}`}>
                                  {getContentTypeLabel(stream.type)} · {stream.name || `#${stream.stream_id}`}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="connection-section">
                  <h3>Appareils / IP récents</h3>
                  {(connectionData.devices || []).length === 0 ? (
                    <div className="connection-empty">Aucune IP enregistrée pour ce client.</div>
                  ) : (
                    <div className="connection-list connection-list--compact">
                      {(connectionData.devices || []).slice(0, 12).map((device) => (
                        <div className="connection-row" key={`${device.id}-${device.ip_address}`}>
                          <div className="connection-row-main">
                            <strong>
                              {device.device_id || device.ip_address || 'Appareil inconnu'}
                              {device.is_active && <span className="connection-live-dot"> actif</span>}
                            </strong>
                            <span>
                              IP {device.ip_address || 'N/A'} · dernière vue {formatDateTime(device.last_seen)}
                            </span>
                            {device.user_agent && <small title={device.user_agent}>{device.user_agent}</small>}
                          </div>
                          <span className={`connection-device-badge ${device.is_authorized ? 'connection-device-badge--ok' : ''}`}>
                            {device.is_authorized ? 'Autorisé' : 'Observé'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="connection-section">
                  <h3>Derniers visionnages</h3>
                  {(connectionData.recent_views || []).length === 0 ? (
                    <div className="connection-empty">Aucun visionnage enregistré depuis l'ajout des stats.</div>
                  ) : (
                    <div className="connection-list connection-list--compact">
                      {(connectionData.recent_views || []).map((view, index) => (
                        <div className="connection-row" key={`${view.content_type}-${view.stream_id}-${view.last_seen}-${index}`}>
                          <div className="connection-row-main">
                            <strong>{view.stream_name || `Contenu #${view.stream_id}`}</strong>
                            <span>
                              {getContentTypeLabel(view.content_type)} · IP {view.ip_address || 'N/A'} · {formatDateTime(view.last_seen)}
                            </span>
                            {view.category_name && <small>{view.category_name}</small>}
                          </div>
                          <span className="connection-device-badge">{view.hits || 1} hit(s)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : null}

            <div className="modal-actions">
              {connectionData && (
                <button
                  className="btn-generate-main"
                  onClick={() => openConnectionModal(connectionModal)}
                  disabled={connectionLoading}
                >
                  Actualiser
                </button>
              )}
              <button className="btn-modal-close" onClick={() => setConnectionModal(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal catégories bloquées */}
      {blockCatModal && (
        <div className="modal-fullscreen" onClick={() => setBlockCatModal(null)}>
          <div className="modal-box-green reseller-block-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🚫 Catégories bloquées</h2>
            <p className="subtitle">
              Client : <strong>{blockCatModal.nom || blockCatModal.xtream_username}</strong> — {blockedCats.length} catégorie(s)
            </p>

            <input
              type="text"
              className="category-search-input"
              placeholder="🔍 Rechercher une catégorie..."
              value={catSearch}
              onChange={(e) => setCatSearch(e.target.value)}
            />

            {!blockLoading && (() => {
              const filtered = allCategories.filter(cat =>
                !catSearch || cat.category_name.toLowerCase().includes(catSearch.toLowerCase())
              );
              const allBlocked = filtered.length > 0 && filtered.every(cat => blockedCats.includes(formatCategoryKey(cat)));
              const noneBlocked = filtered.every(cat => !blockedCats.includes(formatCategoryKey(cat)));
              return (
                <div className="category-bulk-actions">
                  <button
                    className="category-bulk-btn category-bulk-btn--block"
                    onClick={() => handleBlockAllCategories(filtered)}
                    disabled={allBlocked || isClientBusy(blockCatModal.id, 'categories')}
                  >
                    🚫 Tout bloquer ({filtered.length})
                  </button>
                  <button
                    className="category-bulk-btn category-bulk-btn--unblock"
                    onClick={() => handleUnblockAllCategories(filtered)}
                    disabled={noneBlocked || isClientBusy(blockCatModal.id, 'categories')}
                  >
                    ✅ Tout débloquer
                  </button>
                </div>
              );
            })()}

            {blockLoading ? (
              <div className="category-list-loading">Chargement...</div>
            ) : (
              <div className="reseller-category-list">
                {allCategories
                  .filter(cat => !catSearch || cat.category_name.toLowerCase().includes(catSearch.toLowerCase()))
                  .map(cat => {
                    const catKey = formatCategoryKey(cat);
                    const isBlocked = blockedCats.includes(catKey);
                    return (
                      <button
                        type="button"
                        key={catKey}
                        className={`reseller-category-row ${isBlocked ? 'reseller-category-row--blocked' : ''}`}
                        onClick={() => toggleBlockCategory(blockCatModal, cat)}
                        disabled={isClientBusy(blockCatModal.id, 'categories')}
                      >
                        <span className="reseller-category-name">
                          {isBlocked ? '🚫 ' : '✅ '}{getCategoryLabel(cat)}
                          <small>{cat.count || 0} éléments</small>
                        </span>
                        <span className={`reseller-category-state ${isBlocked ? 'reseller-category-state--blocked' : ''}`}>
                          {isBlocked ? 'BLOQUÉE' : 'ACTIVE'}
                        </span>
                      </button>
                    );
                  })}
                {allCategories.filter(cat => !catSearch || cat.category_name.toLowerCase().includes(catSearch.toLowerCase())).length === 0 && (
                  <div className="category-list-loading">Aucune catégorie trouvée</div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-modal-close" onClick={() => setBlockCatModal(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL DE NOTIFICATION PERSONNALISÉ ===== */}
      {notif && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px'
        }} onClick={notif.type !== 'confirm' ? closeNotif : undefined}>
          <div style={{
            background: '#111827', border: `1px solid ${
              notif.type === 'success' ? 'rgba(34,197,94,0.4)' :
              notif.type === 'error' ? 'rgba(239,68,68,0.4)' :
              notif.type === 'warning' ? 'rgba(245,158,11,0.4)' :
              notif.type === 'confirm' ? 'rgba(99,102,241,0.4)' :
              'rgba(148,163,184,0.3)'}`,
            borderRadius: '18px', padding: '28px 32px', maxWidth: '440px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2.2rem', marginBottom: '12px', textAlign: 'center' }}>
              {notif.type === 'success' ? '✅' : notif.type === 'error' ? '❌' :
               notif.type === 'warning' ? '⚠️' : notif.type === 'confirm' ? '❓' : 'ℹ️'}
            </div>
            <div style={{ color: '#F1F5F9', fontWeight: '700', fontSize: '1.1rem', textAlign: 'center', marginBottom: notif.message ? '10px' : '20px' }}>
              {notif.title}
            </div>
            {notif.message && (
              <div style={{ color: '#94A3B8', fontSize: '0.88rem', textAlign: 'center', lineHeight: 1.6, marginBottom: '22px', whiteSpace: 'pre-line' }}>
                {notif.message}
              </div>
            )}
            {notif.type === 'confirm' ? (
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={() => { closeNotif(); notif.onConfirm && notif.onConfirm(); }} style={{
                  flex: 1, padding: '11px', background: 'linear-gradient(135deg,#16A34A,#22C55E)',
                  border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem'
                }}>Confirmer</button>
                <button onClick={closeNotif} style={{
                  flex: 1, padding: '11px', background: 'rgba(148,163,184,0.1)',
                  border: '1px solid rgba(148,163,184,0.2)', borderRadius: '10px', color: '#94A3B8', fontWeight: '600', cursor: 'pointer', fontSize: '0.95rem'
                }}>Annuler</button>
              </div>
            ) : (
              <button onClick={closeNotif} style={{
                width: '100%', padding: '12px', background: `${
                  notif.type === 'success' ? 'linear-gradient(135deg,#16A34A,#22C55E)' :
                  notif.type === 'error' ? 'linear-gradient(135deg,#DC2626,#EF4444)' :
                  notif.type === 'warning' ? 'linear-gradient(135deg,#D97706,#F59E0B)' :
                  'linear-gradient(135deg,#4F46E5,#6366F1)'}`,
                border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700',
                cursor: 'pointer', fontSize: '0.95rem'
              }}>OK</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerDashboard;
